import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { apiGet, apiSend, fmtDate } from '../api';
import { alertFailure } from '../utils/sessionEnd';
// v0.171.0 — site mode: pins queued on this phone show in the register too,
// marked pending, so the list agrees with the plan (see outbox-local.js).
import { addToOutbox, blobToDataUrl, drainOutbox, listOutbox, subscribeOutbox } from '../utils/outbox.js';
import { applyPendingToDefects, isLocalId, pendingPhotoRows, rowIdFromLocalId } from '../utils/outbox-local.js';
import { queuedPhotoView } from '../utils/queuedPhotoView.js';
// v0.259.0 — photos added from the item popup (an item with no plan) queue the
// same way the pin card's do: bytes in the outbox, a queued pin by reference.
import { ref as outboxRef } from '../utils/outbox-refs.js';
import { makePhotoBlobs } from '../utils/imageVariants';
import { mayBePhoto, toBrowserImages } from '../utils/heicPhotos';
import { getCurrentIdentityKey } from '../utils/outbox-identity.js';
// v0.172.0 — reload when the service worker's background refresh finds the
// pin list changed (a save from the plan, or from another device).
import { markApiMutated, subscribeApiUpdates, urlConcerns } from '../utils/apiFreshness.js';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
// v0.312.0 — On hold and Cancelled: one place folds the stored status to a key.
import { normaliseStatus, statusLabel } from '../utils/defectStatus';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
// v0.17.1 — Lucide icons for visual cohesion with InSpecPDF + the
// InSite SaaS suite design language. Replaces the emoji glyphs from
// v0.16.x.
import { FileText, MapPin, Search, MessageCircleQuestion, Smartphone, Camera, Images, Users } from 'lucide-react';
// v0.32.0 — Shared three-button motto component (Drop a Plan / Drop a Pin /
// Send a Report) with pin-themed icons matching the home blueprint hero.
import MottoButtons from '../components/MottoButtons';
import ScanVideosModal from '../components/ScanVideosModal';
import ProjectTeamModal from '../components/ProjectTeamModal';
import { strayLinkHolders } from '../utils/linkHolders';
import { ChatAvatar } from '../components/LiveChat';
import { teamChat } from '../utils/shareTeam.js';
// v0.269.0 — replies this person has not read (per person).
import { useNewReplies, NewReplyChip, RegisterNewReplies } from '../components/NewReplies';
import { useResponseFlags, ResponseRequiredChip, ResponseFlagsReadError } from '../components/ResponseFlags';
import { getCurrentUser } from '../utils/user';
import { hasPreview } from '../utils/preview';
// v0.34.0 — Plans manager modal — opens from the Drop a Plan pill.
import PlansManagerModal from '../components/PlansManagerModal';
// 2026-08-20 — put a folder of site photos back against the items they belong
// to in one pass. Built after a 25-pin inspection lost every photo to the
// offline bug in outbox-refs.js; useful on any inspection, because attaching
// them one item at a time is slow even when nothing has gone wrong.
import BulkPhotoAttachModal from '../components/BulkPhotoAttachModal';
// v0.127.0 — the read-only sheet (unpinned items) gains the same
// conversation thread + reply composer as PlanScreen's editor sheet, plus
// a photo strip.
import DefectConversation from '../components/DefectConversation';
// v0.262.0 — the photo sheet's big photo, with the pen.
import PhotoMarkupStage, { burnMarks } from '../components/PhotoMarkupStage';
// 2026-10-29 (v0.263.0) — the plan's pin card and the row's ⋮ menu.
import DefectEditorSheet from '../components/DefectEditorSheet';
import RowActionsMenu from '../components/RowActionsMenu';
import { saveDefectEdit, deleteDefect } from '../utils/saveDefectEdit.js';
// v0.9.0 — trade catalogue: drives the thumbnail outline colour on each row.
import { tradeColor, tradeLabel } from '../utils/trades';
// 2026-08-20 — pinned-plan delete guard (Midland close-out).
import { drawingDeleteUrl } from '../utils/plan-delete-guard';
// v0.21.0 — applyReportFilters lifted to ProjectsScreen export so
// the Register page can apply the same per-report scope when the
// URL carries ?reportId=N.
import { applyReportFilters } from './ProjectsScreen';

// ---------------------------------------------------------------------------
// v0.9.0 — Trade column (between Status and Assignee). The photo-thumbnail
//          gets a 3px left border in the trade colour so the row reads as
//          'the same thing' as the coloured pin on the canvas.
// v0.9.2 — Three click zones on each row:
//            * photo thumbnail  -> opens <PhotoLightbox> (full-screen overlay)
//            * "Drop a Pin" link -> navigates to PlanScreen w/ #defect-N
//            * rest of the row   -> openDefect() (the current default)
//          stopPropagation on the two specialised zones keeps the row-level
//          handler from firing twice. Description column widened (min-width
//          280px), Location column shrunk (width 110px).
// v0.263.0 — Brett: "you need a different popup for editing the
//          details/fields ie the same one when we edit the pin in plan view.
//          Shall we have the 3 dots at the end of the line". Now:
//            * photo thumbnail  -> the photo sheet (every item, pinned or not)
//            * rest of the row   -> the plan's pin card (DefectEditorSheet)
//            * ⋮ at the end      -> Edit details / Photos / Show on plan / Delete
//          The register's own full-screen viewer is gone; the photo sheet
//          pages, has the strip and the pen.
//
// RegisterScreen — defect register / list view for a single project.
//
// Ported from InSpec FieldDefects.jsx (1544 lines) — the InSpec source is a
// mobile-first card list; DropPin's register is a desktop-leaning sortable
// table with bulk actions. The two diverge enough that this is a structural
// port rather than a copy: we keep the filter/status/priority semantics and
// the normalise() shape, but rebuild the rendering as a table.
//
// What InSpec carried that we DROP for now:
//   • SyncBanner / offline cache / fieldOfflineQueue   — DropPin is online-first
//   • access tiers (own / view / edit / none)          — everyone is admin
//   • subcontractor-by-company-id coupling             — assignee is free-text
//   • the AnnotatableLightbox (photo annotate)         — deferred to v0.7.0
//   • RaiseDefectForm                                  — already in PlanScreen
//   • SubContractor / Trade / CompanyId / role gates   — stripped per spec
//
// What we ADD (per the v0.6.1 spec):
//   • sortable columns                                  — click header, asc/desc
//   • bulk select with shift-click multi-select         — register-only
//   • bulk actions: status / assign / delete            — register-only
//   • CSV export                                        — register-only
//   • keyboard nav (j/k/arrows + Enter to open)         — register-only
//   • empty state with friendly illustration            — kept from InSpec
//
// API contract:
//   GET    /api/defects?projectId=N
//   PATCH  /api/defects/:id
//   DELETE /api/defects/:id
//
// The API returns camelCase; the InSpec source rendered PascalCase. We bridge
// with defectFromApi(), matching PlanScreen.jsx's translation layer verbatim
// so the two screens share a single UI shape.
//
// Route: /projects/:projectId/register
// Routed in App.jsx, linked from ProjectDetailScreen's tile grid.
// ---------------------------------------------------------------------------

function defectFromApi(row) {
  if (!row) return row;
  return {
    id: row.id ?? row.Id,
    projectId: row.projectId,
    drawingId: row.drawingId ?? null,
    defectNumber: row.defectNumber ?? null,
    description: row.description ?? '',
    priority: row.priority ?? 'medium',
    status: row.status ?? 'open',
    // v0.45.1 — Type for unified register filter (defect/rfi/snag/observation).
    type: row.type ?? 'defect',
    assignee: row.assignedTo ?? row.assignee ?? '',
    assignedTo: row.assignedTo ?? null,
    assignedContactId: row.assignedContactId ?? null,
    locationLabel: row.locationLabel ?? null,
    trade: row.trade ?? 'general',
    markX: row.markX,
    markY: row.markY,
    // v0.45.1 — pinCount from API for "+N" register chip.
    pinCount: typeof row.pinCount === 'number' ? row.pinCount : (Array.isArray(row.pins) ? row.pins.length : 0),
    photoCount: row.photoCount ?? (Array.isArray(row.photos) ? row.photos.length : 0),
    previewPhotoUrl: row.previewPhotoUrl
      ?? (Array.isArray(row.photos) && row.photos.length ? (row.photos[0].url || row.photos[0].dataUrl) : null),
    // 2026-09-01 — why the preview is null, when the server could not sign
    // it fresh (see api/defects). Drawn as a labelled placeholder below.
    previewUnavailable: row.previewUnavailable ?? null,
    createdAt: row.createdAt ?? null,
    updatedAt: row.updatedAt ?? null,
    // v0.171.0 — queued on this device, not yet on the server.
    pending: !!row.pending,
    pendingPhotoCount: Number(row.pendingPhotoCount) || 0,
    rectifiedAt: row.rectifiedAt ?? null,
    // v0.115.0 recipient progress (pushed back via /api/report-feedback) —
    // read by the Status pill. Was dropped by this mapper; now carried.
    recipientStatus: row.recipientStatus ?? null,
    recipientBookedDate: row.recipientBookedDate ?? null,
    recipientName: row.recipientName ?? null,
    recipientUpdatedAt: row.recipientUpdatedAt ?? null,
    // v0.122.0 — conversation awareness: role of the NEWEST comment
    // ('recipient' = an unanswered question from the report's recipient)
    // + total count. Fed by the list query's computed columns.
    lastCommentRole: row.lastCommentRole ?? null,
    commentCount: typeof row.commentCount === 'number' ? row.commentCount : 0,
  };
}

// Colour maps — mirror PlanScreen so a pin in the canvas matches its row.
// InSpec used a different palette (critical/high/medium/low + open/in_progress/
// rectified/closed); DropPin trims "closed" to align with the API enum and
// renames "critical" -> "urgent" to match the spec brief.
const PRIORITY_COLOR = {
  urgent: theme.danger,
  high: theme.warn,
  medium: theme.brand,
  low: theme.textLight,
};
// v0.14.0 - 3-state workflow: open / rectified / complete. Replaces
// the old open / in_progress / rectified scheme. The defectStatus util
// (src/utils/defectStatus.js) is the single source of truth for the
// label + colour mapping; the locals here just adapt that to the
// register's theme.* palette.
//
// Legacy 'in_progress' is normalised to 'open' on read so existing
// data keeps rendering; new writes use the 3-state vocabulary.
const STATUS_COLOR = {
  open:      theme.danger,
  // v0.126.0 — rectified is GREEN (success), matching "finished work
  // looks finished". Amber is reserved for the recipient in-progress /
  // scheduled overlay on OPEN items (see statusPillFor). Previously
  // rectified was theme.warn, which made a Rectified pill identical
  // to a scheduled/in-progress one.
  rectified: theme.success,
  complete:  theme.success,
  // v0.312.0 — held (violet, its own token) and called off (grey).
  on_hold:   theme.hold,
  cancelled: theme.textLight,
};
const STATUS_LABEL = {
  open:      'Open',
  on_hold:   'On hold',
  rectified: 'Rectified',
  complete:  'Complete',
  cancelled: 'Cancelled',
};
const PRIORITY_LABEL = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

// v0.115.0 / v0.125.0 / v0.126.0 — one place computes the Status pill.
// Brett: recipient progress belongs IN the status, not beside it. 'done'
// already advances the real Status server-side; the in-between states
// (scheduled / in_progress) can't exist in the LOCKED 3-state data
// model, so an OPEN item wearing one renders it in the pill itself —
// AMBER (theme.warn — work is happening), booked date in the label
// when scheduled, who + when in the tooltip. Data stays open.
// The amber overlay applies ONLY when Status is 'open': rectified and
// complete always render their own colours (green success), never the
// overlay and never a booked date. Extracted from DefectRow in
// v0.125.0 so the phone card layout renders the identical treatment.
// v0.171.0 — a pin that has not synced has no number yet; say "new", never
// leak the 'local:<row>' bookkeeping id into the register.
const displayId = (id) => (isLocalId(id) ? 'new' : id);

// v0.171.0 — the small amber mark beside the status pill for a pin (or a
// pin's photos / edits) still held on this phone.
function NotSyncedChip({ d }) {
  if (!d || !d.pending) return null;
  const n = Number(d.pendingPhotoCount) || 0;
  const title = n > 0
    ? `Saved on this phone — ${n} photo${n === 1 ? '' : 's'} not synced yet`
    : 'Saved on this phone — not synced yet';
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 10, fontWeight: 700, color: theme.warn,
      border: `1px solid ${theme.warn}`, borderRadius: 999, padding: '1px 6px',
      whiteSpace: 'nowrap',
    }}>
      <Smartphone size={10} /> not synced
    </span>
  );
}

function statusPillFor(d) {
  const stKey = normaliseStatus(d.status);
  const rKey = String(d.recipientStatus || '').toLowerCase();
  const rActive = stKey === 'open' && (rKey === 'in_progress' || rKey === 'scheduled');
  const label = rActive
    ? (rKey === 'scheduled'
        ? 'Scheduled' + (d.recipientBookedDate ? ' · ' + new Date(d.recipientBookedDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '')
        : 'In progress')
    : (STATUS_LABEL[stKey] || stKey);
  const color = rActive ? theme.warn : (STATUS_COLOR[stKey] || theme.textLight);
  const title = rActive
    ? (d.recipientName || 'Recipient') + ' — ' + rKey.replace(/_/g, ' ')
      + (d.recipientUpdatedAt ? ', updated ' + new Date(d.recipientUpdatedAt).toLocaleDateString() : '')
    : undefined;
  return { stKey, label, color, title };
}

// v0.122.0 / v0.125.0 — "Question" chip: the newest comment on this
// item is from the report's recipient, still unanswered. Shared by
// the desktop description cell and the phone card's pill row.
function QuestionChip() {
  return (
    <span
      title="Unanswered question from the report's recipient — open to reply"
      style={{
        flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10, fontWeight: 700,
        padding: '2px 7px', borderRadius: 999,
        color: theme.brand,
        border: `1px solid ${theme.brand}`,
        whiteSpace: 'nowrap',
      }}
    >
      <MessageCircleQuestion size={11} strokeWidth={2.4} />
      Question
    </span>
  );
}

// Columns are config-driven so the sort/header/keyboard code only knows
// about (key, label, accessor, width, align). New columns slot in here.
const COLUMNS = [
  // v0.7.3 - leftmost thumbnail column (sort by photoCount when clicked).
  { key: 'preview',      label: 'Photo',       width: 56,  align: 'center', accessor: d => d.photoCount || 0, isPreview: true },
  { key: 'defectNumber', label: '#',          width: 60,  align: 'left',   accessor: d => d.defectNumber ?? d.id },
  // v0.9.2 - Description gets the flex slack (width:null + minWidth in <td>).
  //          Location shrunk to 110px - 'Bathroom 2' / 'Level 3 lobby' fit
  //          comfortably; longer labels can ellipsise.
  { key: 'description',  label: 'Description', width: null, align: 'left', accessor: d => d.description, minWidth: 280 },
  { key: 'priority',     label: 'Priority',    width: 110, align: 'left',  accessor: d => d.priority },
  { key: 'status',       label: 'Status',      width: 130, align: 'left',  accessor: d => d.status },
  { key: 'trade',        label: 'Trade',       width: 110, align: 'left',  accessor: d => d.trade || 'general' },
  { key: 'assignee',     label: 'Assignee',    width: 140, align: 'left',  accessor: d => d.assignee || '' },
  { key: 'locationLabel',label: 'Location',    width: 110, align: 'left',  accessor: d => d.locationLabel || '' },
  // v0.34.1 — Photos column dropped from the register. Brett: too
  // little value vs the column width it eats. The PHOTO column on
  // the left already shows whether photos exist.
  // { key: 'photoCount',   label: 'Photos',      width: 70,  align: 'right', accessor: d => d.photoCount || 0 },
  { key: 'createdAt',    label: 'Created',     width: 120, align: 'left',  accessor: d => d.createdAt || '' },
  { key: 'rectifiedAt',  label: 'Rectified',   width: 120, align: 'left',  accessor: d => d.rectifiedAt || '' },
];

export default function RegisterScreen() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  // v0.249.0 — floor scan is a preview (utils/preview.js): its two buttons, the
  // clip picker and the ?scanVideos=1 link are only for a sign-in the server
  // lists for it, and never in the App Store build.
  const floorScan = hasPreview(getCurrentUser(), 'floor-scan');
  // v0.21.0 — read ?reportId=N from the URL. When present, the
  // register becomes that report's editor: only defects in the
  // report's explicitDefectIds (or matching its filters, for legacy
  // reports) show. New reports start empty.
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId');

  // v0.34.1 — Report scope is mandatory now. If the URL has no
  // reportId, jump to the project's most-recent report so we never
  // show a leaked cross-report defect list. This closes a class of
  // bugs where any nav path that dropped the query param silently
  // fetched every defect on the project.
  useEffect(() => {
    if (reportId || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await apiGet(`/api/reports?projectId=${encodeURIComponent(projectId)}`);
        if (cancelled) return;
        const reports = Array.isArray(list) ? list : [];
        if (reports.length === 0) return; // genuinely no report — leave on project-wide view
        // /api/reports is ORDER BY CreatedAt DESC so [0] is newest.
        const target = reports[0].id ?? reports[0].Id;
        if (target) navigate(`/projects/${projectId}/register?reportId=${target}`, { replace: true });
      } catch (_) { /* leave on project-wide view */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, projectId]);

  const [defects, setDefects] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // v0.34.0 — Plans-manager modal state. Opened from the Drop a Plan
  // pill. planQueue feeds the rename modal (which already exists on
  // ProjectDetailScreen — here we wire a simpler inline rename: the
  // first file goes through the rename modal, subsequent items use
  // their filename as the nickname for now).
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  // v0.207.0 — Brett: "Can you add a spot where i can select the videos from my
  // device and save it on the job for processing. Save me getting out a chord
  // and transferring."
  // v0.207.4 — ?scanVideos=1 opens it straight away. The upload bar in the app
  // shell is shown wherever he is, and tapping it has to land ON the picker for
  // the job the clip belongs to, not merely near it.
  const [scanVideosOpen, setScanVideosOpen] = useState(false);
  const [bulkPhotosOpen, setBulkPhotosOpen] = useState(false);
  // v0.288.0 — Brett: "where is the project team pages?" The register's Team
  // button opens the project team over the register.
  // v0.316.0 — false, true, or 'client' / 'trade': opened from that strip
  // group to add someone straight into its chat.
  const [teamOpen, setTeamOpen] = useState(false);
  useEffect(() => {
    if (floorScan && searchParams.get('scanVideos') === '1') setScanVideosOpen(true);
  }, [searchParams, floorScan]);
  // v0.21.0 — current report (when reportId in URL). null when on
  // the unscoped project register.
  const [report, setReport] = useState(null);
  // v0.21.0 — Add-defects-to-report picker state.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSaving, setPickerSaving] = useState(false);

  // Filters — chips on top of the table. InSpec had three selects; DropPin
  // uses a Status chip row that clicks-to-toggle, plus a drawing dropdown
  // (no equivalent in InSpec — added here because DropPin can have
  // multiple drawings per project).
  // v0.125.0 — Priority filter row REMOVED (Brett's phone review: it ate
  // a whole row and nobody filters by priority here). Priority stays on
  // the data, the defect sheet and the table column — only the filter
  // chips are gone.
  const [filterStatus, setFilterStatus] = useState('all');     // 'all' | 'open' | 'in_progress' | 'rectified'
  const [filterDrawingId, setFilterDrawingId] = useState('all'); // 'all' | 'unpinned' | number
  // v0.45.5 — Type filter removed. RFI/Snag/Observation register
  //           belongs on InSpec, not DropPin.
  const [search, setSearch] = useState('');

  // Sort — { key, dir }. Default newest-first (createdAt desc) which lines
  // up with the InSpec card-list ordering.
  // v0.20.0 — default sort by # ascending (was 'createdAt' desc). Brett:
  // "Order by the number lowest to highest by default." Matches how
  // operators read printed defect lists — lowest number first.
  const [sort, setSort] = useState({ key: 'defectNumber', dir: 'asc' });

  // Selection state for bulk actions. Set<id> + lastClickedId for shift-range.
  const [selected, setSelected] = useState(() => new Set());
  const lastClickedIdRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Keyboard focus row (highlight + Enter-to-open). Stored as id rather than
  // index so it survives filter/sort changes.
  const [focusedId, setFocusedId] = useState(null);

  // v0.172.0 — `quiet`: a background refresh (the service worker found newer
  // data, or a sync finished) must not blank the list behind "Loading…".
  const load = useCallback(async (opts) => {
    if (!projectId) return;
    if (!(opts && opts.quiet)) setLoading(true);
    setError('');
    try {
      const [defectRows, drawingRows] = await Promise.all([
        // v0.22.1 — Scope by reportId when active. Drawings + defects
        // both live on a specific report now.
        apiGet(reportId ? `/api/defects?reportId=${encodeURIComponent(reportId)}` : `/api/defects?projectId=${encodeURIComponent(projectId)}`),
        apiGet(reportId ? `/api/drawings?reportId=${encodeURIComponent(reportId)}` : `/api/drawings?projectId=${encodeURIComponent(projectId)}`).catch(() => []),
      ]);
      // v0.171.0 — lay the queue over the server's list (site mode).
      const rows = await listOutbox().catch(() => []);
      const merged = applyPendingToDefects(Array.isArray(defectRows) ? defectRows : [], rows, { projectId, currentKey: getCurrentIdentityKey() });
      setDefects(merged.map(defectFromApi));
      setDrawings(Array.isArray(drawingRows) ? drawingRows : []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, reportId]);

  useEffect(() => { load(); }, [load]);

  // v0.172.0 — the service worker served a cached list and then found the
  // live one differs: show the live one. Pins dropped on the plan a moment
  // ago used to sit invisible here until the next visit.
  useEffect(() => subscribeApiUpdates((url) => {
    if (urlConcerns(url, ['defects', 'drawings', 'defect-photos'])) load({ quiet: true });
  }), [load]);

  // v0.171.0 — after a sync (or a discard) the queued pins have real ids and
  // the pending marks are stale: reload, debounced so a forty-photo sync is
  // one reload, not forty.
  useEffect(() => {
    let timer = null;
    const unsub = subscribeOutbox((evt) => {
      // v0.173.0 — only a row that reached the server changes what the
      // server would answer; queueing / discarding is local and the pending
      // overlay already covers it. Keeps a sync to a few reloads, not one per
      // queued photo.
      if (!evt || evt.type !== 'applied') return;
      clearTimeout(timer);
      timer = setTimeout(() => { load({ quiet: true }); }, 2000);
    });
    return () => { clearTimeout(timer); unsub(); };
  }, [load]);

  // v0.21.0 — Fetch the report when reportId is in the URL. The
  // report's settingsJson scopes the visible defect list.
  useEffect(() => {
    if (!reportId) { setReport(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await apiGet(`/api/reports/${reportId}`);
        if (!cancelled) setReport(r);
      } catch (e) {
        if (!cancelled) setReport(null);
      }
    })();
    return () => { cancelled = true; };
  }, [reportId]);

  // v0.21.0 — Save the report's settingsJson back to the server when
  // the operator confirms a defect-picker selection. Patches the
  // explicitDefectIds list; everything else on the report row stays.
  const saveReportDefectIds = useCallback(async (nextIds) => {
    if (!report) return;
    setPickerSaving(true);
    try {
      const currentSettings = (typeof report.settingsJson === 'string')
        ? (() => { try { return JSON.parse(report.settingsJson); } catch { return {}; } })()
        : (report.settingsJson || {});
      const nextSettings = { ...currentSettings, explicitDefectIds: Array.from(new Set(nextIds.map(Number))) };
      await apiSend('PATCH', `/api/reports/${report.id}`, { settingsJson: nextSettings });
      setReport(r => r ? { ...r, settingsJson: nextSettings } : r);
    } catch (e) {
      setError('Could not update report: ' + (e.message || String(e)));
    } finally {
      setPickerSaving(false);
    }
  }, [report]);

  // v0.21.1 — Drawings dropdown should match the report's scope.
  // Brett: "Droppin drawings are still in the dropdown from the
  // previous report. should be none for a new project." When viewing
  // a specific report (?reportId=N), the drawings dropdown was
  // listing every drawing on the project — including drawings whose
  // only defects belong to other (closed/sent) reports. Now we scope
  // the dropdown to drawings referenced by defects within this
  // report's scope. New / empty reports → empty dropdown.
  const scopedDefects = useMemo(() => {
    return report ? applyReportFilters(defects, report.settingsJson) : defects;
  }, [defects, report]);
  const availableDrawings = useMemo(() => {
    if (!report) return drawings;
    const ids = new Set();
    scopedDefects.forEach(d => { if (d.drawingId != null) ids.add(Number(d.drawingId)); });
    return drawings.filter(d => ids.has(Number(d.id)));
  }, [drawings, scopedDefects, report]);

  // v0.125.0 — Questions-waiting banner. The recipient of a sent
  // report can push questions per item (v0.122.0 conversation layer);
  // the per-row chip alone gave the client NO signal when the list
  // was long. A defect whose NEWEST comment is from the recipient is
  // an unanswered question. The banner above the filters counts them
  // and toggles a questions-only view of the list.
  const [questionsOnly, setQuestionsOnly] = useState(false);
  const questionItems = useMemo(
    () => scopedDefects.filter(d => d.lastCommentRole === 'recipient'),
    [scopedDefects]
  );
  // Whoever asked: the recipient's name off the defects themselves,
  // falling back to a neutral label when the payload has none.
  const questionAskerName = useMemo(() => {
    const named = questionItems.find(d => d.recipientName);
    return (named && named.recipientName) || 'the builder';
  }, [questionItems]);
  // If the last question gets answered while the view is filtered,
  // release the filter rather than stranding the user on an empty list.
  useEffect(() => {
    if (questionsOnly && questionItems.length === 0) setQuestionsOnly(false);
  }, [questionsOnly, questionItems]);

  // v0.269.0 — new replies, per person. Brett: "i have no idea when a
  // response arrives". Unlike the questions banner (the item's NEWEST
  // comment, the same for everyone) this is what THIS signed-in person has
  // not read: opening the item's conversation clears it for them only. The
  // read is project-wide, so a reply on an item of another report is named
  // too, with a way there, instead of being invisible from this one.
  const newReplies = useNewReplies(projectId);
  const [repliesOnly, setRepliesOnly] = useState(false);
  const scopedIds = useMemo(() => new Set(scopedDefects.map(d => Number(d.id))), [scopedDefects]);
  const repliesHere = useMemo(
    () => newReplies.items.filter(x => scopedIds.has(Number(x.defectId))),
    [newReplies.items, scopedIds]
  );
  const repliesElsewhere = useMemo(() => {
    if (loading) return { count: 0, reportId: null };   // the list is not here yet
    const away = newReplies.items.filter(x => !scopedIds.has(Number(x.defectId)));
    const newest = away
      .filter(x => x.reportId && String(x.reportId) !== String(reportId || ''))
      .sort((a, b) => String(b.latestAt || '').localeCompare(String(a.latestAt || '')))[0];
    return { count: away.length, reportId: newest ? newest.reportId : null };
  }, [newReplies.items, scopedIds, reportId, loading]);
  useEffect(() => {
    if (repliesOnly && repliesHere.length === 0) setRepliesOnly(false);
  }, [repliesOnly, repliesHere]);

  // v0.321.0 — messages flagged "response required" by the office. Brett: "I
  // need a button and indicator that screams out that there is an
  // outstanding response required." The same for everyone in the office
  // (not per person, unlike new replies); it stays until someone clears it
  // from the message's ... (v0.322.0: the card's banner and its Mark responded
  // are gone). A client's or subbie's own flag (v0.322.0) shows here too.
  // v0.322.0 - the red "N items need a response" line above the filters is
  // gone too (Brett: "remove this one too"); the red chip on the row is the mark.
  const responseFlags = useResponseFlags(projectId);

  // v0.21.1 — If the previously-selected drawing isn't in this
  // report's scope anymore, reset the filter so the operator isn't
  // stuck on an empty list.
  useEffect(() => {
    if (filterDrawingId === 'all' || filterDrawingId === 'unpinned') return;
    const stillThere = availableDrawings.some(d => Number(d.id) === Number(filterDrawingId));
    if (!stillThere) setFilterDrawingId('all');
  }, [availableDrawings, filterDrawingId]);

  // ------------- derived: filtered + sorted list -------------
  const visible = useMemo(() => {
    // v0.21.0 — Apply the report's scope FIRST. If we're viewing
    // a specific report (?reportId=N), only defects in that report's
    // settingsJson (explicitDefectIds, or legacy filters) are visible
    // here. The status/priority/drawing chips then filter further
    // within that report's scope.
    let rows = report ? applyReportFilters(defects, report.settingsJson) : defects;

    if (filterStatus !== 'all') {
      // v0.14.0 - normalise legacy values when filtering. Filtering for
      // "Open" also catches legacy 'in_progress' rows; filtering for
      // "Complete" also catches legacy 'closed' rows.
      // v0.312.0 — through normaliseStatus, so On hold / Cancelled filter too.
      rows = rows.filter(d => normaliseStatus(d.status) === filterStatus);
    }
    // v0.125.0 — Priority filter removed (UI only; the column stays).
    // v0.45.5 — Type filter removed (RFI workflow → InSpec).
    // v0.125.0 — Questions-only view: the banner above the filters
    // toggles this to answer the builder's open questions in one hit.
    if (questionsOnly) rows = rows.filter(d => d.lastCommentRole === 'recipient');
    // v0.269.0 — only the items with replies this person has not read.
    if (repliesOnly) rows = rows.filter(d => newReplies.byDefect.has(Number(d.id)));
    // v0.321.0 — only the items holding a message flagged "response required".
    if (filterDrawingId === 'unpinned') {
      rows = rows.filter(d => d.drawingId == null);
    } else if (filterDrawingId !== 'all') {
      rows = rows.filter(d => Number(d.drawingId) === Number(filterDrawingId));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(d =>
        (d.description || '').toLowerCase().includes(q) ||
        (d.assignee || '').toLowerCase().includes(q) ||
        (d.locationLabel || '').toLowerCase().includes(q) ||
        String(d.defectNumber ?? displayId(d.id)).toLowerCase().includes(q)
      );
    }

    const col = COLUMNS.find(c => c.key === sort.key);
    if (col) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const av = col.accessor(a);
        const bv = col.accessor(b);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
    }
    return rows;
  }, [defects, filterStatus, filterDrawingId, search, sort, report, questionsOnly, repliesOnly, newReplies.byDefect]);

  // ------------- counters for the header subtitle -------------
  // v0.14.0 - 3-state counts. Open / Rectified / Complete. Legacy
  // 'in_progress' rolls into 'open' (treated as not-yet-fixed).
  const counts = useMemo(() => {
    // v0.125.0 — display-only split of the open count: an open item
    // whose RecipientStatus is in_progress / scheduled is "with the
    // builder" (they've picked it up or booked it), so the header
    // reads e.g. "10 open (8 with Rawson Homes)" instead of implying
    // nothing is happening. The 3-state Status data is untouched.
    let open = 0, onHold = 0, rectified = 0, complete = 0, cancelled = 0, withRecipient = 0, recipientName = null;
    for (const d of defects) {
      const k = normaliseStatus(d.status);
      if (k === 'rectified') rectified++;
      else if (k === 'complete') complete++;
      else if (k === 'on_hold') onHold++;
      else if (k === 'cancelled') cancelled++;
      else {
        open++;
        const r = String(d.recipientStatus || '').toLowerCase();
        if (r === 'in_progress' || r === 'scheduled') {
          withRecipient++;
          if (!recipientName && d.recipientName) recipientName = d.recipientName;
        }
      }
    }
    return { open, onHold, rectified, complete, cancelled, withRecipient, recipientName, total: defects.length };
  }, [defects]);

  // ------------- selection helpers (incl. shift-click range) -------------
  const visibleIds = useMemo(() => visible.map(d => d.id), [visible]);

  const toggleOne = (id, ev) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (ev && ev.shiftKey && lastClickedIdRef.current != null) {
        // Range select between lastClickedId and id in *currently visible*
        // order. Both endpoints land selected; everything between joins them.
        const a = visibleIds.indexOf(lastClickedIdRef.current);
        const b = visibleIds.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) next.add(visibleIds[i]);
          lastClickedIdRef.current = id;
          return next;
        }
      }
      if (next.has(id)) next.delete(id); else next.add(id);
      lastClickedIdRef.current = id;
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(prev => {
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  };

  const clearSelection = () => { setSelected(new Set()); lastClickedIdRef.current = null; };

  // ------------- bulk actions -------------
  const bulkPatch = async (body, label) => {
    if (!selected.size || bulkBusy) return;
    // v0.171.0 — a pin that has not synced has no server id to patch. Sync
    // first; say so rather than queue a request that can only be refused.
    const ids = Array.from(selected).filter(id => !isLocalId(id));
    if (ids.length < selected.size) {
      alert(`${selected.size - ids.length} of the selected pins ${selected.size - ids.length === 1 ? 'has' : 'have'} not synced yet — sync first to edit ${selected.size - ids.length === 1 ? 'it' : 'them'} in bulk.`);
      if (!ids.length) return;
    }
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map(id => apiSend('PATCH', `/api/defects/${id}`, body))
    );
    const failed = results.filter(r => r.status === 'rejected');
    setBulkBusy(false);
    if (failed.length) {
      // TODO_DROPPIN: replace with a real toast / inline error list.
      alertFailure(`${label} failed for ${failed.length} of ${ids.length} defects. The rest succeeded.`, failed[0].reason);
    }
    clearSelection();
    load();
  };

  const bulkStatus = (status) => {
    const label = `Status -> ${STATUS_LABEL[status] || status}`;
    bulkPatch({ status }, label);
  };

  const bulkAssign = () => {
    // TODO_DROPPIN: swap prompt() for a proper contacts picker once the
    // contacts API exists; for now a free-text prompt mirrors PlanScreen.
    const name = prompt(`Assign ${selected.size} defect(s) to:`);
    if (name == null) return;                       // user cancelled
    bulkPatch({ assignedTo: name.trim() || null }, 'Bulk assign');
  };

  const bulkDelete = async () => {
    if (!selected.size || bulkBusy) return;
    if (!confirm(`Delete ${selected.size} defect(s)? This cannot be undone.`)) return;
    // v0.171.0 — unsynced pins are deleted from the plan sheet, not in bulk.
    const ids = Array.from(selected).filter(id => !isLocalId(id));
    if (ids.length < selected.size) {
      alert(`${selected.size - ids.length} of the selected pins ${selected.size - ids.length === 1 ? 'has' : 'have'} not synced yet — open ${selected.size - ids.length === 1 ? 'it' : 'them'} on the plan to delete.`);
      if (!ids.length) return;
    }
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map(id => apiSend('DELETE', `/api/defects/${id}`))
    );
    const failed = results.filter(r => r.status === 'rejected');
    setBulkBusy(false);
    if (failed.length) {
      // TODO_DROPPIN: replace alert with toast.
      alertFailure(`Delete failed for ${failed.length} of ${ids.length} defects.`, failed[0].reason);
    }
    clearSelection();
    load();
  };

  // ------------- CSV export -------------
  const exportCsv = () => {
    const headers = ['#', 'Description', 'Priority', 'Status', 'Trade', 'Assignee', 'Location', 'Photos', 'Created', 'Rectified'];
    const lines = [headers.join(',')];
    visible.forEach(d => {
      const row = [
        d.defectNumber ?? displayId(d.id),
        d.description ?? '',
        d.priority ?? '',
        d.status ?? '',
        d.trade ?? 'general',
        d.assignee ?? '',
        d.locationLabel ?? '',
        d.photoCount ?? 0,
        d.createdAt ?? '',
        d.rectifiedAt ?? '',
      ].map(csvEscape).join(',');
      lines.push(row);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `defects-project-${projectId}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ------------- keyboard navigation (j/k + arrows + Enter) -------------
  useEffect(() => {
    function onKey(e) {
      // Skip when typing in an input/textarea/select — search field would
      // otherwise eat j/k as nav.
      const tag = (e.target && e.target.tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      if (!visible.length) return;

      const idx = focusedId == null ? -1 : visibleIds.indexOf(focusedId);
      let next = idx;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        next = idx < 0 ? 0 : Math.min(visible.length - 1, idx + 1);
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        next = idx < 0 ? 0 : Math.max(0, idx - 1);
      } else if (e.key === 'Enter' && focusedId != null) {
        const d = defects.find(x => x.id === focusedId);
        if (d) openDefect(d);
        return;
      } else if (e.key === 'x' && focusedId != null) {
        // Quick-toggle selection for the focused row, mirroring Gmail.
        toggleOne(focusedId, null);
        return;
      } else {
        return;
      }
      e.preventDefault();
      const targetId = visibleIds[next];
      setFocusedId(targetId);
      // Scroll the focused row into view if needed.
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-defect-row="${targetId}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, visibleIds, focusedId, defects]);

  // ------------- row click → the item card -------------
  // v0.263.0 — every item opens the plan's pin card in place (Brett: "the
  // same one when we edit the pin in plan view"); a pinned item no longer
  // jumps to the plan — that is ⋮ Show on plan. The thumbnail and ⋮ Photos
  // open the photo sheet.
  const [openSheetDefect, setOpenSheetDefect] = useState(null);
  const [editDefect, setEditDefect] = useState(null);
  // v0.45.22 — Brett (screenshot showing "2 of 6" pagination on a
  // single-drawing report): every register → plan navigation must
  // carry the reportId so PlanScreen scopes its drawings list to
  // the report (instead of falling back to all project drawings).
  // Hash is preserved for #defect-N deep-link focus.
  const planUrlFor = (d) =>
    `/projects/${projectId}/plan${d && d.drawingId ? `/${d.drawingId}` : ''}` +
    (reportId ? `?reportId=${encodeURIComponent(reportId)}` : '') +
    (d && d.id ? `#defect-${d.id}` : '');

  const openDefect = (d) => { setFocusConvId(null); setEditDefect(d); };
  // v0.268.0 — the client-reply email's "Open the item" lands here with
  // #defect-N: that item's card opens once, scrolled to its conversation.
  // Re-read on hashchange (a second email's link in the same tab). An item no
  // longer on this report says so rather than opening nothing.
  const [focusConvId, setFocusConvId] = useState(null);
  const hashOpened = useRef('');
  useEffect(() => {
    const openFromHash = () => {
      const h = window.location.hash || '';
      const m = /^#defect-(\d+)$/.exec(h);
      if (!m || hashOpened.current === h || loading) return;
      hashOpened.current = h;
      const d = defects.find(x => String(x.id) === m[1]);
      if (!d) { setError('The item from that link is not on this report — it may have been deleted or moved.'); return; }
      setFocusConvId(d.id);
      setEditDefect(d);
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [defects, loading]);
  const openPhotos = (d) => setOpenSheetDefect(d);
  const showOnPlan = (d) => navigate(planUrlFor(d));

  const removeDefect = async (d) => {
    if (!confirm('Delete this item? This can\'t be undone.')) return;
    try {
      await deleteDefect(d);
    } catch (err) {
      alertFailure('Could not delete item: ' + err.message, err);
      return;
    }
    setDefects(prev => prev.filter(x => String(x.id) !== String(d.id)));
    setEditDefect(null);
    load({ quiet: true });
  };

  // The card's Save. A failure is thrown back to the card, which shows it in
  // place and keeps what was typed.
  const saveEdit = async (form) => {
    await saveDefectEdit(editDefect, form);
    drainOutbox();
    setEditDefect(null);
    load({ quiet: true });
  };

  // ⋮ at the end of each row and card. Show on plan only for a pinned item.
  const rowActions = (d) => [
    { key: 'edit', label: 'Edit details', onSelect: () => openDefect(d) },
    { key: 'photos', label: 'Photos', onSelect: () => openPhotos(d) },
    ...(d.drawingId ? [{ key: 'plan', label: 'Show on plan', onSelect: () => showOnPlan(d) }] : []),
    { key: 'delete', label: 'Delete', danger: true, onSelect: () => removeDefect(d) },
  ];

  // v0.39.1 (PlanScreen) — project contacts for the card's Assignee list.
  // v0.298.0 — the same list draws the team strip under the header, so its
  // read is named: 'loading' | 'ok' | 'failed'. A failed read keeps whatever
  // was there and says so; it is never shown as "no one on the team".
  const [projectContacts, setProjectContacts] = useState([]);
  const [teamState, setTeamState] = useState('loading');
  const [teamReadAt, setTeamReadAt] = useState(0);
  // v0.339.0 — Brett: "Location shows nothing. Do it properly." The team is
  // the open REPORT's (/api/report-contacts). With no report open there is no
  // team: the strip and the Team button are not drawn (never "no one").
  useEffect(() => {
    if (!projectId || !reportId) { setProjectContacts([]); return undefined; }
    let cancelled = false;
    setTeamState('loading');
    (async () => {
      try {
        const list = await apiGet(`/api/report-contacts?reportId=${encodeURIComponent(reportId)}`);
        if (cancelled) return;
        setProjectContacts(Array.isArray(list) ? list : []);
        setTeamState('ok');
      } catch { if (!cancelled) setTeamState('failed'); }
    })();
    return () => { cancelled = true; };
  }, [projectId, reportId, teamReadAt]);
  const onTeamChange = useCallback((list) => {
    setProjectContacts(list);
    setTeamState('ok');
  }, []);

  // v0.300.0 — who holds a live link but is not on the team, counted on the
  // strip so a stray or test link is seen (Brett: "otherwise there is no way of
  // easily knowing"). 'loading' | 'ok' | 'failed'; a failed read says so on the
  // strip, never "nobody".
  const [projectLinks, setProjectLinks] = useState(null);
  const [linksState, setLinksState] = useState('loading');
  const [linksReadAt, setLinksReadAt] = useState(0);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLinksState('loading');
    (async () => {
      try {
        const rows = await apiGet(`/api/report-shares?projectId=${encodeURIComponent(projectId)}`, { fresh: true });
        if (cancelled) return;
        // v0.339.0 — only this report's links are weighed against its team.
        const all = Array.isArray(rows) ? rows : [];
        setProjectLinks(reportId ? all.filter(r => String(r.reportId) === String(reportId)) : all);
        setLinksState('ok');
      } catch { if (!cancelled) setLinksState('failed'); }
    })();
    return () => { cancelled = true; };
  }, [projectId, reportId, linksReadAt]);
  const strayHolders = useMemo(
    () => (teamState === 'ok' && projectLinks ? strayLinkHolders(projectLinks, projectContacts) : null),
    [teamState, projectLinks, projectContacts],
  );

  // ------------- pin-link click → navigate to plan -------------
  // Separate handler so the click zone has its own stopPropagation path; the
  // row-level onClick must NOT also fire openDefect (which would race with
  // navigate and double-up the route push).
  const onPinLinkClick = (d, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    // v0.45.22 — reportId preserved via planUrlFor (see above).
    if (d.drawingId) {
      navigate(planUrlFor(d));
    } else {
      navigate(`/projects/${projectId}/plan${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`);
    }
  };

  // ------------- header click -> sort toggle -------------
  const onHeaderClick = (key) => {
    setSort(prev => {
      if (prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  // ------------- render -------------
  return (
    <div>
      {/* v0.45.8 / v0.45.10 — Right-aligned action row.
          Settings + Archive sit alongside Done. Settings deep-links
          to ReportScreen with the panel pre-opened; Archive PATCHes
          the report's settingsJson with archived:true (no schema
          change). Both are gated to report-scoped views since they
          act on a specific report.
          v0.307.0 — Brett: "Shift the archive button to be in settings".
          Archive now lives in the Report settings panel (ReportScreen). */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {reportId && (
          <button
            type="button"
            onClick={() => setTeamOpen(true)}
            style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="People on this report's team"
          >
            <Users size={14} aria-hidden /> Team
          </button>
        )}
        {reportId && (
          <button
            onClick={() => navigate(`/projects/${projectId}/report?reportId=${encodeURIComponent(reportId)}&settings=open`)}
            style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Report settings (and Archive)"
          >
            <span aria-hidden>⚙</span> Settings
          </button>
        )}
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <span aria-hidden>✓</span> Done
        </button>
      </div>

      {/* Header card — title + single count line + three motto buttons. */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <PageHeading>
              {report ? (report.name || 'Report') : 'Defect Register'}
            </PageHeading>
            {/* v0.45.4 — Drop the orange "Report scope · N of M" span.
                Brett: "Delete the orange report text, its a double up."
                The four sub-counts below already imply the report's
                visible-defect scope; the bold uppercase repeat was
                visual noise. Single muted count line stays. */}
            {/* v0.125.0 — open count carries a display-only "(N with
                {recipient})" split so booked / in-progress pushback
                from the builder reads here, not just per-row. */}
            <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4 }}>
              {counts.open} open{counts.withRecipient > 0 && (
                <span> ({counts.withRecipient} with {counts.recipientName || 'the builder'})</span>
              )}{counts.onHold > 0 && <span> &middot; {counts.onHold} on hold</span>} &middot; {counts.rectified} rectified &middot; {counts.complete} complete
              {counts.cancelled > 0 && <span> &middot; {counts.cancelled} cancelled</span>}
            </div>
            {/* 2026-08-20 — a report is worth nothing without its images, and
                attaching them one item at a time is slow even when nothing has
                gone wrong. Sits next to the counts because "how many items have
                no photo" is the same question as the counts answer. */}
            {defects.length > 0 && (
              <button
                onClick={() => setBulkPhotosOpen(true)}
                title="Pick this inspection's photos and attach them to the items in one pass"
                style={{
                  marginTop: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700,
                  border: `1px solid ${theme.brand}`, borderRadius: 4,
                  background: theme.surface, color: theme.brand, cursor: 'pointer',
                }}
              >Add photos in bulk</button>
            )}
          </div>
          {/* v0.30.0 / v0.32.0 — Three motto buttons.
              v0.32.0: switched from custom inline buttons to the shared
              <MottoButtons> component so the trio carries the same
              pin-themed iconography everywhere it appears (this screen,
              the report screen, anywhere else we drop it in). Brett:
              "We lost our motto buttons... can our pins be like the
              pins on the cover page?" */}
          {/* v0.34.0 — Drop a Plan now opens an inline modal that
              shows the existing plans (with names + nicknames) AND
              accepts new drops. The pill carries a count badge so
              the user sees "3 plans" without clicking. */}
          {/* v0.202.0 — SCAN FLOOR, beside Drop a Pin, on the screen Brett was
              actually looking at: "It shouldnt be there it should be within the
              report next to drop a pin." onScanFloor is opt-in on MottoButtons,
              so ReportScreen and PlansManagerModal are unchanged. It is a full
              page load rather than navigate() because the survey is a static
              page under /public, not a route in this app — which is also why it
              touches no DropPin state. */}
          <MottoButtons
            onDropPlan={() => setPlansModalOpen(true)}
            onDropPin={() => navigate(`/projects/${projectId}/plan${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`)}
            // v0.307.0 — Brett: "Hide the scan floor and scan videos". The two
            // buttons are off; the survey page and ?scanVideos=1 are untouched.
            onScanFloor={SHOW_SCAN_BUTTONS && floorScan ? () => {
              // v0.203.0 — Brett: "alter it so it goes straight into the survey.
              // We already have a plan loaded remember". Hand the drawing this
              // inspection is on straight to the survey so it opens ready to
              // walk, instead of asking him to load a plan he has already
              // loaded. Prefers the drawing he has filtered to, else the first.
              // Read-only use of drawings that are already in state — no fetch,
              // no write, nothing else in DropPin is touched.
              const pick = (drawings || []).find(d => Number(d.id) === Number(filterDrawingId))
                || (drawings || [])[0] || null;
              // rasterUrl FIRST: api/drawings returns sourceUrl for the original
              // upload, which for a PDF plan is a PDF, and an <img> cannot show
              // one. rasterUrl is the rendered page — PlanScreen writes it back
              // the first time a PDF plan is opened (PlanScreen.jsx:440) — and a
              // data URL beats a signed blob URL because it needs no network.
              const src = pick && (pick.rasterUrl || pick.FileDataUrl || pick.fileDataUrl || pick.sourceUrl || null);
              const q = new URLSearchParams();
              if (src) q.set('plan', src);
              if (pick && pick.id != null) q.set('planId', String(pick.id));
              const nm = (report && report.name) || '';
              if (nm) q.set('name', nm);
              // v0.207.5 — AND WHICH JOB, so the survey can show the footage
              // already uploaded to it. Without this the page has a plan and no
              // way to ask what was filmed on it, which is the gap Brett hit:
              // "you need to show a strip for the videos i have saved".
              if (projectId != null) q.set('projectId', String(projectId));
              if (reportId != null) q.set('reportId', String(reportId));
              const qs = q.toString();
              window.location.href = '/kitchen-floor-survey.html' + (qs ? '?' + qs : '');
            } : undefined}
            onScanVideos={SHOW_SCAN_BUTTONS && floorScan ? () => setScanVideosOpen(true) : undefined}
            onSendReport={() => navigate(`/projects/${projectId}/report${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`)}
            primaryKey="report"
            counts={{ plan: drawings.length, pin: defects.length }}
          />
        </div>

        {/* v0.125.0 — Questions-waiting banner. Prominent but in the
            brand vocabulary (same tint/border family as the pills).
            Tap = filter the list to just the questioned items; tap
            again = show everything. */}
        {questionItems.length > 0 && (
          <button
            onClick={() => setQuestionsOnly(v => !v)}
            title={questionsOnly ? 'Showing questions only — tap to show all items' : 'Tap to see just the questioned items'}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', marginTop: 14, padding: '10px 14px',
              background: questionsOnly ? theme.brand : hexAlpha(theme.brand, 0.08),
              border: `1.5px solid ${theme.brand}`,
              borderRadius: 8,
              color: questionsOnly ? 'white' : theme.brandDark,
              fontSize: 13, fontWeight: 600, textAlign: 'left',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <MessageCircleQuestion size={18} strokeWidth={2.2} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              {questionItems.length} question{questionItems.length === 1 ? '' : 's'} from {questionAskerName} need{questionItems.length === 1 ? 's' : ''} an answer
            </span>
            <span style={{
              flexShrink: 0, fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.85,
            }}>
              {questionsOnly ? 'Showing questions · tap for all' : 'Tap to view'}
            </span>
          </button>
        )}

        {/* v0.322.0 — no line for flags (the row's chip is the mark); a failed
            read still says so, so no chip never passes for "none flagged". */}
        <ResponseFlagsReadError readErr={responseFlags.readErr} />

        {/* v0.269.0 — new replies this person has not read. */}
        <RegisterNewReplies
          count={repliesHere.length}
          onlyThese={repliesOnly}
          onToggle={() => setRepliesOnly(v => !v)}
          elsewhere={repliesElsewhere}
          onOpenReport={(rid) => navigate(`/projects/${projectId}/register?reportId=${encodeURIComponent(rid)}`)}
          readErr={newReplies.readErr}
        />

        {/* Filter chips — Status row, Drawing select, search */}
        {/* v0.125.0 — Priority ChipGroup + divider removed (see the
            filter-state comment above). Status keeps the row to
            itself. */}
        {/* v0.45.5 — Type ChipGroup removed (RFI workflow → InSpec). */}
        <div style={{ display: 'flex', gap: 32, marginTop: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <ChipGroup
            label="Status"
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: 'all',       label: 'All' },
              { value: 'open',      label: 'Open',      color: STATUS_COLOR.open },
              { value: 'on_hold',   label: 'On hold',   color: STATUS_COLOR.on_hold },
              { value: 'rectified', label: 'Rectified', color: STATUS_COLOR.rectified },
              { value: 'complete',  label: 'Complete',  color: STATUS_COLOR.complete },
              { value: 'cancelled', label: 'Cancelled', color: STATUS_COLOR.cancelled },
            ]}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <FilterLabel>Drawing</FilterLabel>
            <select
              className="ss-field"
              value={filterDrawingId}
              onChange={e => setFilterDrawingId(e.target.value === 'all' || e.target.value === 'unpinned' ? e.target.value : Number(e.target.value))}
              style={{ ...input, width: 220, maxWidth: '100%', padding: '6px 10px', fontSize: 12 }}
            >
              {/* v0.21.1 — `availableDrawings` is scoped to the
                  current report when ?reportId is present, so a fresh
                  empty report shows no drawings here. The default
                  "All drawings" + "Unpinned only" entries are still
                  available so the operator can flip filters without
                  the dropdown going completely blank. */}
              <option value="all">All drawings{report && availableDrawings.length === 0 ? ' (none in this report)' : ''}</option>
              <option value="unpinned">Unpinned only</option>
              {availableDrawings.map(d => (
                <option key={d.id} value={d.id}>
                  {d.title || d.filename || `Drawing #${d.id}`}
                </option>
              ))}
            </select>
          </div>
          {/* v0.307.0 — Brett: "shorten the search box, shift the team down
              lower". The search box is a fixed width and the team sits on this
              row, to its right. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 280, maxWidth: '100%' }}>
            <FilterLabel>Search</FilterLabel>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search description, assignee, location..."
              style={{ ...input, padding: '6px 10px', fontSize: 12 }}
            />
          </div>
          {/* v0.298.0 — the project team, beside the filters (v0.307.0: moved
              down from the Status row). */}
          {reportId && <div data-part="register-team" style={{ marginLeft: 'auto', minWidth: 0 }}>
            <TeamStrip
              state={teamState}
              members={projectContacts}
              onOpen={() => setTeamOpen(true)}
              onAdd={(chat) => setTeamOpen(chat)}
              onRetry={() => setTeamReadAt(n => n + 1)}
              linksState={linksState}
              stray={strayHolders}
              onRetryLinks={() => setLinksReadAt(n => n + 1)}
            />
          </div>}
        </div>
      </div>

      {/* Bulk-action bar — only when there's a selection */}
      {selected.size > 0 && (
        <div style={{ ...card, marginBottom: 12, padding: 12, borderColor: theme.brand, background: theme.bg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.brandDark }}>
              {selected.size} selected
            </div>
            {/* v0.14.0 - 3-state bulk actions. "Mark In Progress" is
                dropped (the 3-state workflow doesn't have that
                intermediate step); "Mark Complete" is added. */}
            <button onClick={() => bulkStatus('open')}      disabled={bulkBusy} style={btnSecondary}>Mark Open</button>
            <button onClick={() => bulkStatus('rectified')} disabled={bulkBusy} style={btnSecondary}>Mark Rectified</button>
            <button onClick={() => bulkStatus('complete')}  disabled={bulkBusy} style={btnSecondary}>Mark Complete</button>
            <button onClick={() => bulkStatus('on_hold')}   disabled={bulkBusy} style={btnSecondary}>Mark On hold</button>
            <button onClick={() => bulkStatus('cancelled')} disabled={bulkBusy} style={btnSecondary}>Mark Cancelled</button>
            <button onClick={() => {
              const ids = Array.from(selected).join(',');
              navigate(`/projects/${projectId}/report?defectIds=${ids}`);
            }} disabled={bulkBusy || !selected.size} style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Open the report pre-filtered to these defects">
              <FileText size={14} /> Selected to PDF
            </button>
            <button onClick={bulkAssign} disabled={bulkBusy} style={btnSecondary}>Assign...</button>
            <button onClick={bulkDelete} disabled={bulkBusy} style={{ ...btnSecondary, color: theme.danger, borderColor: theme.danger }}>
              Delete
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={clearSelection} style={{ ...btnSecondary, fontSize: 12 }}>Clear</button>
          </div>
        </div>
      )}

      {/* Body — error / loading / empty / table */}
      {error && (
        <div style={{ ...card, color: theme.danger, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: theme.textLight }}>
          Loading defects...
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          hasAny={defects.length > 0}
          onClearFilters={() => { setFilterStatus('all'); setFilterDrawingId('all'); setSearch(''); setQuestionsOnly(false); setRepliesOnly(false); }}
        />
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {/* v0.125.0 — desktop table. Hidden on phones (responsive.css
              .ss-reg-table) where the stacked card list below renders
              instead; the sideways-scrolling table hid the Status
              column so rectified work looked untouched. */}
          <div className="ss-reg-table" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
                  <th style={{ width: 40, padding: '10px 8px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={visibleIds.length > 0 && visibleIds.every(id => selected.has(id))}
                      onChange={toggleAllVisible}
                      title="Select all visible"
                    />
                  </th>
                  {COLUMNS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => onHeaderClick(col.key)}
                      style={{
                        width: col.width || undefined,
                        minWidth: col.minWidth || undefined,
                        padding: '10px 10px',
                        textAlign: col.align,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: theme.textLight,
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                      }}
                      title={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {sort.key === col.key && (
                        <span style={{ marginLeft: 4, color: theme.brand }}>
                          {sort.dir === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </th>
                  ))}
                  <th style={{ width: 44, padding: '10px 4px' }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map(d => (
                  <DefectRow
                    key={d.id}
                    defect={d}
                    selected={selected.has(d.id)}
                    focused={focusedId === d.id}
                    onToggle={(ev) => toggleOne(d.id, ev)}
                    onOpen={() => openDefect(d)}
                    onHoverFocus={() => setFocusedId(d.id)}
                    onOpenPhotos={() => openPhotos(d)}
                    actions={rowActions(d)}
                    newReplies={newReplies.byDefect.get(Number(d.id))?.replies || 0}
                    responseFlag={responseFlags.byDefect.get(Number(d.id)) || null}
                    onPinLinkClick={(ev) => onPinLinkClick(d, ev)}
                    onPatchLocation={async (id, locationLabel) => {
                      await apiSend('PATCH', `/api/defects/${id}`, { locationLabel });
                      setDefects(prev => prev.map(x => x.id === id ? { ...x, locationLabel } : x));
                    }}
                    onPatchDescription={async (id, description) => {
                      await apiSend('PATCH', `/api/defects/${id}`, { description });
                      setDefects(prev => prev.map(x => x.id === id ? { ...x, description } : x));
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* v0.125.0 — phone card list. display:none inline; the
              640px media query in responsive.css flips the two so a
              phone gets stacked cards (thumbnail / description /
              status-pill row) with every signal on screen at once:
              3-state status, amber in-progress/scheduled recipient
              treatment with booked date, and the Question chip. */}
          <div className="ss-reg-cards" style={{ display: 'none' }}>
            {visible.map(d => (
              <DefectCardMobile
                key={d.id}
                defect={d}
                selected={selected.has(d.id)}
                onToggle={(ev) => toggleOne(d.id, ev)}
                onOpen={() => openDefect(d)}
                onOpenPhotos={() => openPhotos(d)}
                actions={rowActions(d)}
                newReplies={newReplies.byDefect.get(Number(d.id))?.replies || 0}
                responseFlag={responseFlags.byDefect.get(Number(d.id)) || null}
              />
            ))}
          </div>
          <div style={{ padding: '8px 14px', fontSize: 11, color: theme.textLight, borderTop: `1px solid ${theme.border}`, background: theme.bg }}>
            Showing {visible.length} of {defects.length}
          </div>
        </div>
      )}

      {/* Photo sheet — every item, from the thumbnail or ⋮ Photos (v0.263.0). */}
      {openSheetDefect && (
        <DefectPhotoSheet
          defect={openSheetDefect}
          onClose={() => setOpenSheetDefect(null)}
          onPhotosQueued={() => load({ quiet: true })}
        />
      )}

      {/* v0.263.0 — the plan's pin card, opened by a row tap or ⋮ Edit details.
          v0.287.0 — Previous / Next step through the rows as filtered here, and
          the register keeps its place on the item that is open. */}
      {editDefect && (
        <DefectEditorSheet
          key={editDefect.id}
          defect={editDefect}
          projectContacts={projectContacts}
          drawings={drawings}
          onSave={saveEdit}
          onDelete={() => removeDefect(editDefect)}
          onCancel={() => setEditDefect(null)}
          items={visible}
          onOpenItem={(d) => {
            setFocusedId(d.id);
            openDefect(d);
            requestAnimationFrame(() => {
              const el = document.querySelector(`[data-defect-row="${d.id}"]`);
              if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
            });
          }}
          focusConversation={focusConvId === editDefect.id}
        />
      )}

      {/* v0.21.2 — Picker modal mount removed. Component definition
          retained below for one cycle pending Brett's decision on
          whether curated defect lists return in a different shape. */}

      {/* v0.34.0 — Plans manager modal. Opens from the Drop a Plan pill.
          Lists current drawings, accepts new drops. Drops navigate to
          the plan canvas with the file pre-queued — that screen owns
          the PDF→PNG conversion + rename modal flow. */}
      {bulkPhotosOpen && (
        <BulkPhotoAttachModal
          defects={[...defects].sort((a, b) => (a.defectNumber || 0) - (b.defectNumber || 0))}
          onClose={() => setBulkPhotosOpen(false)}
          onDone={({ queued, failed }) => {
            // Say what happened. The whole reason this screen exists is that
            // photos went missing without anything being said.
            if (failed) alert(`${queued} photo${queued === 1 ? '' : 's'} queued. ${failed} could not be read and were NOT attached.`);
            load();
          }}
        />
      )}
      {teamOpen && reportId && (
        <ProjectTeamModal
          projectId={projectId}
          reportId={reportId}
          addTo={typeof teamOpen === 'string' ? teamOpen : null}
          onChange={onTeamChange}
          onLinksChange={() => setLinksReadAt(n => n + 1)}
          onClose={() => setTeamOpen(false)}
        />
      )}
      {floorScan && scanVideosOpen && (
        <ScanVideosModal
          projectId={projectId}
          reportId={reportId}
          onClose={() => setScanVideosOpen(false)}
        />
      )}
      {plansModalOpen && (
        <PlansManagerModal
          projectId={projectId}
          reportId={reportId}
          onImported={load}
          drawings={drawings.map(d => ({
            Id: d.id || d.Id,
            Title: d.name || d.Title || '',
            FileName: d.fileName || d.FileName || '',
            FileDataUrl: d.rasterUrl || d.sourceUrl || d.FileDataUrl || null,
          }))}
          onAcceptFiles={async (files) => {
            // Hand off to PlanScreen which already owns the
            // PDF→PNG conversion + planQueue + PlanRenameModal flow.
            // We stash the files on window so PlanScreen can pick
            // them up on mount; falls back to plain navigation when
            // browser doesn't allow this (rare).
            try {
              window.__droppin_pending_plans__ = files;
            } catch (_) {}
            setPlansModalOpen(false);
            navigate(`/projects/${projectId}/plan${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`);
          }}
          onRename={async (id, name) => {
            await apiSend('PATCH', `/api/drawings/${id}`, { name });
            setDrawings(prev => prev.map(d => (d.id === id || d.Id === id)
              ? { ...d, name, Title: name }
              : d));
          }}
          onDelete={async (id, opts) => {
            // 2026-08-20 — a pinned plan answers 409 unless opts.unpin, and the
            // modal relays the server's true pin count before retrying with it.
            // URL logic in src/utils/plan-delete-guard.js (tested).
            await apiSend('DELETE', drawingDeleteUrl(id, !!(opts && opts.unpin)));
            setDrawings(prev => prev.filter(d => d.id !== id && d.Id !== id));
            load(); // pins may have been stripped — re-read so rows show unpinned truthfully
          }}
          onClose={() => setPlansModalOpen(false)}
        />
      )}
    </div>
  );
}

// v0.21.0 — Report defect picker. Modal listing every defect on the
// project with a checkbox per row. Pre-ticks the defects already in
// the report. On Save calls onSave(nextIds) with the new list.
function ReportDefectPicker({ allDefects, currentIds, saving, onCancel, onSave }) {
  const [selected, setSelected] = useState(() => new Set((currentIds || []).map(Number)));
  const [search, setSearch] = useState('');
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allDefects;
    return allDefects.filter(d =>
      String(d.defectNumber ?? displayId(d.id)).toLowerCase().includes(q) ||
      (d.description || '').toLowerCase().includes(q) ||
      (d.trade || '').toLowerCase().includes(q) ||
      (d.status || '').toLowerCase().includes(q)
    );
  }, [allDefects, search]);
  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(Number(id))) next.delete(Number(id));
      else next.add(Number(id));
      return next;
    });
  }
  return (
    <div
      onClick={() => !saving && onCancel()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, borderRadius: 10, width: '100%', maxWidth: 760, maxHeight: '88vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Add defects to this report</div>
            <div style={{ fontSize: 11, color: theme.textLight, marginTop: 2 }}>
              Tick the project defects this report should include.
            </div>
          </div>
          <button onClick={onCancel} disabled={saving} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: theme.textLight }}>✕</button>
        </div>
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${theme.border}` }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by # / description / trade / status…"
            style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `1px solid ${theme.border}`, borderRadius: 6, boxSizing: 'border-box' }}
            autoFocus
          />
        </div>
        <div style={{ padding: 14, overflowY: 'auto', flex: 1, background: theme.bg }}>
          {visible.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.textLight, textAlign: 'center', padding: '32px 0' }}>
              {allDefects.length === 0
                ? 'No defects on this project yet — drop pins on a plan first.'
                : 'No defects match the search.'}
            </div>
          ) : visible.map(d => {
            const isOn = selected.has(Number(d.id));
            return (
              <label key={d.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', cursor: 'pointer',
                background: isOn ? theme.bg : theme.surface,
                borderRadius: 6, marginBottom: 4,
                border: `1px solid ${isOn ? theme.brand : theme.border}`,
              }}>
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggle(d.id)}
                  style={{ flexShrink: 0, width: 16, height: 16, margin: 0 }}
                />
                <span style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, fontFamily: 'monospace', minWidth: 36 }}>
                  #{d.defectNumber ?? displayId(d.id)}
                </span>
                <span style={{ flex: 1, fontSize: 13, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.description || <em style={{ color: theme.textLight }}>(no description)</em>}
                </span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: theme.bg, color: theme.brand, fontWeight: 600 }}>
                  {statusLabel(d.status)}
                </span>
              </label>
            );
          })}
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: theme.surface }}>
          <span style={{ fontSize: 12, color: theme.textLight }}>
            {selected.size} of {allDefects.length} selected
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} disabled={saving} style={btnSecondary}>Cancel</button>
            <button
              onClick={() => onSave(Array.from(selected))}
              disabled={saving}
              style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : `Save (${selected.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// v0.34.1 — Inline location editor. Click to flip into an input,
// Enter or blur commits via the parent's onSave (PATCH /api/defects/:id).
// Esc cancels.
//
// v0.35.0 — Adds an AI ✨ button that fills the field from the
// defect description + drawing context. Endpoint /api/ai-suggest-location.
// Falls back silently if AI isn't configured server-side.
function InlineLocation({ value, onSave, aiContext }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => { setDraft(value || ''); }, [value, editing]);

  async function commit(nextValue) {
    const next = String(nextValue ?? draft ?? '').trim();
    if (next === (value || '').trim()) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(next); setEditing(false); }
    catch (_) { setEditing(false); }
    finally { setBusy(false); }
  }

  async function suggest() {
    if (!aiContext?.description || aiBusy || busy) return;
    setAiBusy(true);
    try {
      const r = await fetch('/api/ai-suggest-location', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aiContext),
      });
      const j = await r.json().catch(() => ({}));
      if (j?.ok && j.suggestion) {
        // Fill the draft so the user sees the suggestion + can edit
        // before committing. If the user wasn't editing, flip into
        // edit mode so they see what we proposed.
        setDraft(j.suggestion);
        setEditing(true);
      }
    } catch (_) { /* silent — feature is best-effort */ }
    finally { setAiBusy(false); }
  }

  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 40 }}>
        <span
          onClick={() => setEditing(true)}
          title="Click to edit"
          style={{
            cursor: 'text',
            color: value ? theme.text : theme.textLight,
            borderBottom: `1px dotted ${theme.border}`,
            paddingBottom: 1,
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >{value || 'Add location…'}</span>
        {aiContext?.description && (
          <button
            onClick={suggest}
            disabled={aiBusy}
            title="Suggest a location using AI"
            style={{
              background: 'transparent', border: 'none', padding: '0 2px',
              cursor: aiBusy ? 'wait' : 'pointer',
              fontSize: 13, lineHeight: 1, color: aiBusy ? theme.textLight : theme.brand,
            }}
          >{aiBusy ? '…' : '✨'}</button>
        )}
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, width: '100%' }}>
      <input
        autoFocus
        value={draft}
        disabled={busy || aiBusy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        onBlur={() => { if (!aiBusy) commit(); }}
        placeholder="e.g. Kitchen, Bay 12, Level 3 lobby"
        style={{
          padding: '3px 6px', border: `1px solid ${theme.brand}`,
          borderRadius: 4, fontSize: 13, fontFamily: 'inherit',
          flex: 1, minWidth: 90,
          background: (busy || aiBusy) ? theme.bg : theme.surface,
        }}
      />
      {aiContext?.description && (
        <button
          onMouseDown={(e) => e.preventDefault() /* keep input focused */}
          onClick={suggest}
          disabled={aiBusy || busy}
          title="Suggest a location using AI"
          style={{
            background: 'transparent', border: 'none', padding: '0 2px',
            cursor: aiBusy ? 'wait' : 'pointer',
            fontSize: 13, lineHeight: 1, color: aiBusy ? theme.textLight : theme.brand,
          }}
        >{aiBusy ? '…' : '✨'}</button>
      )}
    </span>
  );
}

// v0.139.0 — InlineDescription. Brett: "we should also be able to edit the text."
// Same commit discipline as InlineLocation (Enter or blur saves, Esc abandons),
// but the description is the row's own click-to-open zone, so editing starts
// from an explicit pencil rather than a click on the words — otherwise you
// could never open an item by clicking its description again.
function InlineDescription({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value || ''); }, [value, editing]);

  async function commit() {
    const next = String(draft ?? '').trim();
    if (next === (value || '').trim()) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(next); setEditing(false); }
    catch (_) { setEditing(false); }
    finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || <em style={{ color: theme.textLight }}>(no description)</em>}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          title="Edit the description"
          aria-label="Edit the description"
          style={{
            flexShrink: 0, background: 'transparent', border: 'none', padding: '0 2px',
            cursor: 'pointer', fontSize: 12, lineHeight: 1, color: theme.textLight,
          }}
        >✎</button>
      </span>
    );
  }

  return (
    <input
      autoFocus
      value={draft}
      disabled={busy}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
      }}
      onBlur={commit}
      placeholder="Describe the item…"
      style={{
        padding: '3px 6px', border: `1px solid ${theme.brand}`,
        borderRadius: 4, fontSize: 13, fontFamily: 'inherit',
        flex: 1, minWidth: 90,
        background: busy ? theme.bg : theme.surface, color: theme.text,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Row — one <tr>. Pulled out so the parent re-render doesn't churn every row
// when only focus / selection changes; we only re-render the row whose props
// actually change. (Not memoised yet — list sizes are O(100) in practice.)
function DefectRow({ defect: d, selected, focused, onToggle, onOpen, onHoverFocus, onOpenPhotos, actions, onPinLinkClick, onPatchLocation, onPatchDescription, newReplies, responseFlag }) {
  const priColor = PRIORITY_COLOR[d.priority] || theme.textLight;
  // v0.125.0 — pill treatment (3-state + amber recipient progress)
  // computed by the shared statusPillFor() so the phone card layout
  // renders the identical pill. See the helper's comment block.
  const { label: pillLabel, color: pillColor, title: pillTitle } = statusPillFor(d);

  return (
    <tr
      data-defect-row={d.id}
      onMouseEnter={onHoverFocus}
      onClick={(e) => {
        // v0.9.2 - row-level click is the default "edit" zone. The photo cell
        // and the Drop-a-Pin link both stopPropagation on their own clicks so
        // they never reach here. We still guard checkbox/button just in case.
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON')) return;
        onOpen();
      }}
      style={{
        borderBottom: `1px solid ${theme.border}`,
        background: selected ? theme.bg : 'transparent',
        boxShadow: focused ? `inset 3px 0 0 ${theme.brand}` : 'none',
        cursor: 'pointer',
      }}
    >
      <td style={{ padding: '8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => {}}                  // controlled via onClick to capture shiftKey
          onClick={(e) => { onToggle(e); }}
        />
      </td>
      {/* v0.7.3 - preview thumbnail column.
          v0.9.2 - photo cell is now its own click zone. stopPropagation keeps
          the row's onOpen from firing; the cell click opens <PhotoLightbox>
          when there's a previewPhotoUrl. Empty thumb falls through to the
          row-level handler so click-anywhere still opens the defect.
          v0.261.0 - an item with no pin falls through too: its photo sheet
          shows every photo AND adds more (Brett: "I cant now click on the
          thumbnail to upload more to that same point").
          v0.263.0 - the thumbnail opens the photo sheet for EVERY item, with
          or without a photo or a pin; the row tap opens the item card. */}
      <td
        style={{ padding: '6px 8px', textAlign: 'center', cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onOpenPhotos && onOpenPhotos();
        }}
        title="Photos — view and add"
      >
        {d.previewPhotoUrl ? (
          <img
            loading="lazy"
            src={d.previewPhotoUrl}
            alt={`Defect ${d.defectNumber ?? displayId(d.id)} photo`}
            title={tradeLabel(d.trade)}
            style={{
              width: 40, height: 40, borderRadius: 6,
              objectFit: 'cover', display: 'block',
              border: `1px solid ${theme.border}`,
              borderLeft: `3px solid ${tradeColor(d.trade)}`,
              margin: '0 auto',
            }}
          />
        ) : (
          <div
            title={d.previewUnavailable
              ? `Photo unavailable — ${d.previewUnavailable}`
              : 'No photos'}
            style={{
              width: 40, height: 40, borderRadius: 6,
              background: theme.bg,
              border: d.previewUnavailable
                ? '1px dashed var(--danger, #dc2626)'
                : `1px solid ${theme.border}`,
              borderLeft: `3px solid ${tradeColor(d.trade)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: d.previewUnavailable ? 'var(--danger, #dc2626)' : theme.textLight,
              fontSize: 16, lineHeight: 1,
              margin: '0 auto',
              userSelect: 'none',
            }}
          >
            <span role="img" aria-label={d.previewUnavailable ? 'Photo unavailable' : 'No photo'}>
              {d.previewUnavailable ? '\u26A0' : '\uD83D\uDCF7'}
            </span>
          </div>
        )}
      </td>
      <td style={{ padding: '8px 10px', color: theme.textLight, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {d.defectNumber != null ? `#${d.defectNumber}` : `#${displayId(d.id)}`}
      </td>
      <td style={{ padding: '8px 10px', maxWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {/* v0.20.0 — pin button next to description; click goes to
              the pin on the plan. v0.45.8 — Icon swapped to a small
              version of the v0.45.5 pushpin SVG (sphere head + stem +
              shadow), trade-coloured. Pinned defects show the full
              pushpin; unpinned defects show a faded version so the
              "drop a pin" CTA still reads. */}
          <button
            onClick={(e) => onPinLinkClick && onPinLinkClick(e)}
            title={d.drawingId != null ? 'Go to this pin on the plan' : 'Drop a pin for this defect — opens the plan picker'}
            aria-label={d.drawingId != null ? 'Go to pin' : 'Drop a pin'}
            style={{
              flexShrink: 0,
              width: 24, height: 28,
              border: 'none', padding: 0,
              background: 'transparent',
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              opacity: d.drawingId != null ? 1 : 0.5,
            }}
          >
            <svg width="22" height="28" viewBox="0 0 40 60" style={{ overflow: 'visible', display: 'block' }} aria-hidden>
              <ellipse cx="12" cy="56" rx="6" ry="1.6" fill="rgba(0,0,0,0.32)" />
              <line x1="12" y1="55" x2="22" y2="24" stroke="#5b6470" strokeWidth="2.6" strokeLinecap="round" />
              <line x1="12.5" y1="54" x2="21.5" y2="24" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" strokeLinecap="round" />
              <circle cx="22" cy="18" r="12" fill={tradeColor(d.trade)} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
              <ellipse cx="18" cy="13" rx="4.5" ry="2.8" fill="rgba(255,255,255,0.55)" />
            </svg>
          </button>
          <InlineDescription
            value={d.description}
            onSave={(v) => onPatchDescription(d.id, v)}
          />
          {/* v0.122.0 — the newest comment on this item is a QUESTION from
              the report's recipient, still unanswered. Subtle chip; open
              the item to read + reply (conversation in the defect sheet).
              v0.125.0 — extracted to the shared <QuestionChip/>. */}
          {d.lastCommentRole === 'recipient' && <QuestionChip />}
          <NewReplyChip count={newReplies} />
        </div>
        {/* v0.321.0 — its own line under the words, so the description is
            not squeezed to "Gap under t…" by a long red chip beside it. */}
        {responseFlag && (
          <div style={{ marginTop: 4, paddingLeft: 32 }}>
            <ResponseRequiredChip flag={responseFlag} />
          </div>
        )}
      </td>
      <td style={{ padding: '8px 10px' }}>
        <Pill color={priColor}>{PRIORITY_LABEL[d.priority] || d.priority}</Pill>
      </td>
      <td style={{ padding: '8px 10px' }}>
        <span title={pillTitle}><Pill color={pillColor}>{pillLabel}</Pill></span>
        <NotSyncedChip d={d} />
      </td>
      <td style={{ padding: '8px 10px' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 11, fontWeight: 600, color: theme.text,
          whiteSpace: 'nowrap',
        }} title={`Trade: ${tradeLabel(d.trade)}`}>
          <span style={{
            width: 9, height: 9, borderRadius: '50%',
            background: tradeColor(d.trade),
            border: '1px solid rgba(0,0,0,0.08)',
            display: 'inline-block',
          }} />
          {tradeLabel(d.trade)}
        </span>
      </td>
      <td style={{ padding: '8px 10px', color: theme.text }}>
        {d.assignee || <span style={{ color: theme.textLight }}>—</span>}
      </td>
      {/* v0.34.1 — Location cell is inline-editable. v0.35.0 — adds
          an AI-suggest ✨ button that fills the field from the
          defect description + drawing context. */}
      <td style={{ padding: '8px 10px', color: theme.text }} onClick={(e) => e.stopPropagation()}>
        <InlineLocation
          value={d.locationLabel}
          onSave={(v) => onPatchLocation(d.id, v)}
          aiContext={{
            description: d.description || '',
            drawingTitle: d.drawingTitle || '',
            drawingNumber: d.drawingNumber || '',
            discipline: d.discipline || '',
          }}
        />
      </td>
      {/* v0.34.1 — Photos td removed (Photos column dropped from
          COLUMNS). The PHOTO thumbnail on the left already conveys
          whether photos exist. */}
      <td style={{ padding: '8px 10px', color: theme.textLight, whiteSpace: 'nowrap' }}>
        {fmtDate(d.createdAt)}
      </td>
      <td style={{ padding: '8px 10px', color: theme.textLight, whiteSpace: 'nowrap' }}>
        {fmtDate(d.rectifiedAt)}
      </td>
      <td style={{ padding: '4px 6px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
        <RowActionsMenu items={actions} />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// v0.125.0 — Phone card for one defect. The desktop table's sideways
// scroll buried everything right of Description on a phone; each card
// puts the whole story on screen at once: thumbnail (trade-coloured
// edge, taps to the photo sheet), #number + description (2-line clamp),
// then a pill row with the 3-state status — including the amber
// in-progress/scheduled recipient treatment + booked date from
// statusPillFor() — and the Question chip when the recipient's
// question is still unanswered. Checkbox keeps bulk-select usable;
// tapping anywhere else opens the same item card the table row does, and
// the ⋮ at the right holds the same menu (v0.263.0).
function DefectCardMobile({ defect: d, selected, onToggle, onOpen, onOpenPhotos, actions, newReplies, responseFlag }) {
  const pill = statusPillFor(d);
  return (
    <div
      data-defect-row={d.id}
      onClick={(e) => {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON')) return;
        onOpen();
      }}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 12px',
        borderBottom: `1px solid ${theme.border}`,
        background: selected ? theme.bg : 'transparent',
        cursor: 'pointer',
      }}
    >
      {/* Selection — same toggleOne path as the table (shift-range
          included, though on a phone it's mostly single taps). */}
      <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, display: 'inline-flex', padding: '14px 2px 8px 0' }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => {}}
          onClick={(e) => onToggle(e)}
          style={{ width: 18, height: 18, margin: 0 }}
        />
      </span>
      {/* Thumbnail — trade-coloured left edge, taps to the photo sheet
          (v0.263.0: every item, photo or not, pinned or not). */}
      <span
        onClick={(e) => {
          e.stopPropagation();
          onOpenPhotos && onOpenPhotos();
        }}
        style={{ flexShrink: 0 }}
        title="Photos — view and add"
      >
        {d.previewPhotoUrl ? (
          <img
            loading="lazy"
            src={d.previewPhotoUrl}
            alt={`Defect ${d.defectNumber ?? displayId(d.id)} photo`}
            style={{
              width: 52, height: 52, borderRadius: 6,
              objectFit: 'cover', display: 'block',
              border: `1px solid ${theme.border}`,
              borderLeft: `3px solid ${tradeColor(d.trade)}`,
            }}
          />
        ) : (
          <span
            title="No photos"
            style={{
              width: 52, height: 52, borderRadius: 6,
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderLeft: `3px solid ${tradeColor(d.trade)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.textLight, fontSize: 18, lineHeight: 1,
              userSelect: 'none',
            }}
          >
            <span role="img" aria-label="No photo">{'📷'}</span>
          </span>
        )}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {/* #number rides inside the clamp box so it shares the first
            line; the whole description clamps to 2 lines. */}
        <span style={{
          fontSize: 13, lineHeight: 1.35, color: theme.text,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          <span style={{ fontWeight: 700, color: theme.textLight, marginRight: 6 }}>
            #{d.defectNumber ?? displayId(d.id)}
          </span>
          {d.description || <em style={{ color: theme.textLight }}>(no description)</em>}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span title={pill.title}><Pill color={pill.color}>{pill.label}</Pill></span>
          <NotSyncedChip d={d} />
          {d.lastCommentRole === 'recipient' && <QuestionChip />}
          <ResponseRequiredChip flag={responseFlag} />
          <NewReplyChip count={newReplies} />
        </span>
      </span>
      <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
        <RowActionsMenu items={actions} />
      </span>
    </div>
  );
}

// v0.262.0 — the photo sheet's Save over original and Restore original.
const SAVE_OVER_TIMEOUT_MS = 60000;

// A save-over or restore goes straight to the server. apiSend would queue it in
// hold mode, and this change must be seen to land (or fail) while the person is
// looking at it.
async function sendPhotoPatch(id, body) {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), SAVE_OVER_TIMEOUT_MS);
  let r;
  try {
    r = await fetch(`/api/defect-photos/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctl.signal,
    });
  } catch (e) {
    throw new Error(e?.name === 'AbortError'
      ? 'No answer after a minute. Close the photos and open them again to see whether it saved.'
      : 'No connection, so nothing was changed.');
  } finally {
    clearTimeout(timer);
  }
  if (!r.ok) {
    let msg = `The photo could not be saved (${r.status}).`;
    try { const j = await r.json(); if (j?.error) msg = j.error; } catch (_e) { /* no body */ }
    throw new Error(msg);
  }
  markApiMutated();
  return r.json().catch(() => null);
}

// ---------------------------------------------------------------------------
// Photo sheet for an item with no pin — slides up from the bottom on a phone,
// a centred card on a desktop. The item's own fields are edited on its row.
//
// v0.127.0 — Brett's phone report: a builder-pushed (unpinned) item with a
// Question chip opened THIS sheet, which had no photos and no conversation —
// the question was invisible and unanswerable. The sheet gained the photos
// (hydrated from /api/defect-photos, seeded from the row's previewPhotoUrl so
// something shows instantly) and the shared <DefectConversation>.
//
// v0.260.0 — Brett, on v0.259's sheet: "this is not a professional page. It
// should only be for adding and previewing photos simiar to when we add a
// photo to a pin" and "i also need drag and drop". It is now the pin card's
// photo box: the item named in a header bar, its photos as the same 88 px
// tiles opening the same contained viewer, Take photo / Choose photos, and a
// dashed box that takes images dragged from the computer. The meta row, the
// Open Plan note and the empty comment box went. The conversation stays ONLY
// while the client has a question waiting — the v0.127.0 case, which has no
// other place to be answered for an item with no pin.
//
// v0.262.0 — Brett, on v0.261: "this should be a larger photo with the
// filmstrip at the bottom. I also have markup ability of the photo and save as
// a copy or save over the original." The selected photo now fills the sheet
// (PhotoMarkupStage: zoom, paging, a red pen), the photos run along a strip
// under it with Take photo / Choose photos at its end, and the strip is the
// drop box. Save as copy queues a new photo like any other added here (works
// offline). Save over original replaces the photo's files on the server and
// keeps the FIRST original beside them, so Restore original puts it back; it
// needs a connection, because a queued overwrite that lands after other edits
// could replace the wrong picture.
//
// v0.263.0 — opens for EVERY item now, pinned too, from the thumbnail and from
// ⋮ Photos; the register's own full-screen viewer went. The item's fields are
// edited in the shared card (DefectEditorSheet — ⋮ Edit details or a row tap).
function DefectPhotoSheet({ defect, onClose, onPhotosQueued }) {
  const [sheetPhotos, setSheetPhotos] = useState(() =>
    defect.previewPhotoUrl ? [{ id: 'preview', url: defect.previewPhotoUrl }] : []
  );
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loadErr, setLoadErr] = useState(null);

  // v0.261.0 — Brett, after dragging six photos in: "When it does open it also
  // only shows the one photo". All six reached the server, about ten seconds
  // apart; the sheet showed only what the server had when it opened, and a
  // photo still waiting on this device vanished once the sheet was closed. Now
  // the sheet is the server's photos PLUS this item's photos still in the
  // outbox (the pin card's way, queuedPhotoView), read again whenever the
  // queue changes — so a photo stays in view from the moment it is added,
  // marked "not synced" until it is sent. Server first, queue second: a photo
  // sent between the two reads is missed until the next read, never doubled.
  const objectUrlsRef = useRef([]);
  useEffect(() => () => { objectUrlsRef.current.forEach(u => URL.revokeObjectURL(u)); }, []);
  const readSeqRef = useRef(0);
  const readPhotos = useCallback(async () => {
    const seq = ++readSeqRef.current;
    let server = null;
    if (!isLocalId(defect.id)) {
      try {
        const rows = await apiGet(`/api/defect-photos?defectId=${encodeURIComponent(defect.id)}`);
        server = (Array.isArray(rows) ? rows : [])
          .map(r => ({
            id: r.id ?? r.Id,
            url: r.displayUrl || r.url || r.dataUrl || r.photoUrl || r.PhotoUrl,
            // v0.262.0 — the full-size file, for burning marks in at full size.
            fullUrl: r.url || null,
            thumbnailUrl: r.thumbnailUrl || null,
            caption: r.caption || null,
            hasOriginal: !!r.hasOriginal,
          }))
          .filter(p => !!(p.url || p.thumbnailUrl));
        if (seq === readSeqRef.current) setLoadErr(null);
      } catch (e) {
        console.warn('[DropPin] could not load sheet photos:', e?.message || e);
        if (seq === readSeqRef.current) setLoadErr("This item's saved photos could not be loaded — close it and open it again.");
      }
    }
    const queued = [];
    try {
      const rows = await listOutbox();
      for (const r of pendingPhotoRows(rows, defect.id, getCurrentIdentityKey())) {
        if (seq !== readSeqRef.current) return;
        const p = await queuedPhotoView(r, objectUrlsRef.current);
        if (p.url || p.thumbnailUrl) queued.push(p);
      }
    } catch (_e) { /* nothing readable in the queue */ }
    if (seq !== readSeqRef.current) return;
    // A server read that failed keeps the server photos already shown.
    setSheetPhotos(prev => [...(server ?? prev.filter(p => !p.pending)), ...queued]);
  }, [defect.id]);
  useEffect(() => {
    readPhotos();
    let timer = null;
    const off = subscribeOutbox((evt) => {
      if (evt && evt.type === 'progress') return;
      clearTimeout(timer);
      timer = setTimeout(readPhotos, 250);
    });
    return () => { off(); clearTimeout(timer); readSeqRef.current += 1; };
  }, [readPhotos]);

  // Photos just added (or a marked-up copy) are shown as soon as they are in
  // the list: they join at its end.
  const showNewestRef = useRef(false);
  const seenCountRef = useRef(sheetPhotos.length);
  useEffect(() => {
    const n = sheetPhotos.length;
    if (showNewestRef.current && n > seenCountRef.current) {
      showNewestRef.current = false;
      setSelectedIdx(n - 1);
    } else if (selectedIdx > n - 1) {
      setSelectedIdx(Math.max(0, n - 1));
    }
    seenCountRef.current = n;
  }, [sheetPhotos, selectedIdx]);

  // v0.259.0 — add photos without a plan. Brett 2026-10-29, on site: "Cant the
  // photo thumbnail link to the camera entry popup, sio you can have photos
  // without a plan?" Each photo is queued in the outbox (synced on Sync now /
  // Wi-Fi like every other site-mode change) and shown here at once from the
  // phone's copy.
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const [preparing, setPreparing] = useState(0);
  const [photoDrop, setPhotoDrop] = useState(false);
  const [photoErr, setPhotoErr] = useState(null);

  // A pin still waiting to sync has no real id yet: point at its queued create.
  const defectKey = isLocalId(defect.id) ? outboxRef(rowIdFromLocalId(defect.id)) : Number(defect.id);
  const queuePhoto = (v, caption) => addToOutbox('POST', '/api/defect-photos', {
    defectId: defectKey,
    caption: caption || null,
    width: v.width || null,
    height: v.height || null,
  }, { blobs: { url: v.original, thumbnailUrl: v.thumbnail, displayUrl: v.display } });
  const afterQueued = () => {
    showNewestRef.current = true;
    readPhotos(); // shown from the queue at once
    drainOutbox();
    onPhotosQueued?.();
  };

  // The picker, the camera and a drop all come through here. A Windows drag
  // often carries no type, so mayBePhoto lets a typeless or HEIC-named file in.
  const ingestPhotoFiles = async (fileList) => {
    const picked = Array.from(fileList || []).filter(f => f && mayBePhoto(f));
    if (!picked.length) return;
    setPhotoErr(null);
    setPreparing(n => n + picked.length);
    const unreadable = [];
    let queued = 0;
    try {
      const { files, failed } = await toBrowserImages(picked);
      unreadable.push(...failed);
      for (const f of files) {
        try {
          await queuePhoto(await makePhotoBlobs(f), f.name);
          queued += 1;
        } catch (err) {
          console.warn('[DropPin] could not queue photo:', err?.message || err);
          unreadable.push(f.name || 'A photo');
        }
      }
    } finally {
      setPreparing(n => Math.max(0, n - picked.length));
    }
    if (queued > 0) afterQueued();
    if (unreadable.length) {
      setPhotoErr(`${unreadable.join(', ')} could not be read. Save ${unreadable.length === 1 ? 'it' : 'them'} as a JPEG and add ${unreadable.length === 1 ? 'it' : 'them'} again.`);
    }
  };
  const onPhotoFiles = (e) => { const list = Array.from(e.target.files || []); e.target.value = ''; ingestPhotoFiles(list); };
  // A drop anywhere on the card is taken; one that misses the card is
  // swallowed, or the browser would leave DropPin to show the image.
  const onSheetDrop = (e) => { e.preventDefault(); e.stopPropagation(); setPhotoDrop(false); ingestPhotoFiles(e.dataTransfer && e.dataTransfer.files); };
  const swallowDrop = (e) => { e.preventDefault(); };
  // v0.319.0 — over the chat's box the file is the chat's: the box lights up
  // and takes the drop, and the item's photos stay dark (Brett: "build it so
  // i can drag photos into chat").
  const overChatBox = (e) => !!(e.target && e.target.closest && e.target.closest('[data-part="composer"][data-drop]'));

  // v0.262.0 — the pen. The stage hands over the strokes; the marks are burnt
  // into the full-size file here, then saved one of two ways.
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const selected = sheetPhotos[selectedIdx] || null;
  const onSave = async (mode, strokes) => {
    const p = selected;
    if (!p || p.pending || busy) return false;
    setSaveErr(null);
    if (mode === 'over' && typeof navigator !== 'undefined' && navigator.onLine === false) {
      setSaveErr('Saving over the original needs a connection. Save as copy works offline.');
      return false;
    }
    setBusy(true);
    try {
      let burnt;
      try {
        burnt = await burnMarks(p.fullUrl || p.url, strokes);
      } catch (e) {
        if (!p.fullUrl || p.fullUrl === p.url) throw e;
        burnt = await burnMarks(p.url, strokes); // the display copy, if the full file will not read
      }
      const base = (p.caption || 'Photo').replace(/\.[a-z0-9]+$/i, '');
      const file = new File([burnt.blob], `${base} (marked up).jpg`, { type: 'image/jpeg' });
      const v = await makePhotoBlobs(file);
      if (mode === 'copy') {
        await queuePhoto(v, file.name);
        afterQueued();
        return true;
      }
      const [url, displayUrl, thumbnailUrl] = await Promise.all([
        blobToDataUrl(v.original),
        v.display ? blobToDataUrl(v.display) : null,
        v.thumbnail ? blobToDataUrl(v.thumbnail) : null,
      ]);
      await sendPhotoPatch(p.id, {
        url, displayUrl, thumbnailUrl,
        width: v.width || null, height: v.height || null,
        keepOriginal: true,
      });
      await readPhotos();
      onPhotosQueued?.();
      return true;
    } catch (e) {
      console.warn('[DropPin] could not save the marked-up photo:', e?.message || e);
      setSaveErr(e?.message || 'The marked-up photo could not be saved.');
      return false;
    } finally {
      setBusy(false);
    }
  };
  const onRestore = async () => {
    const p = selected;
    if (!p || busy) return false;
    setSaveErr(null);
    setBusy(true);
    try {
      await sendPhotoPatch(p.id, { restoreOriginal: true });
      await readPhotos();
      onPhotosQueued?.();
      return true;
    } catch (e) {
      setSaveErr(e?.message || 'The original could not be restored.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const count = sheetPhotos.length;
  const prev = () => setSelectedIdx(i => Math.max(0, i - 1));
  const next = () => setSelectedIdx(i => Math.min(count - 1, i + 1));

  // Esc closes, the arrow keys page — not while drawing, and not while typing
  // a reply in the conversation below.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (editing) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') setSelectedIdx(i => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setSelectedIdx(i => Math.min(count - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, count, onClose]);

  // The selected photo kept in view along the strip.
  const stripRef = useRef(null);
  useEffect(() => {
    const el = stripRef.current?.querySelector('[data-strip-selected]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedIdx, count]);

  const canDraw = !!selected && !selected.pending && selected.id !== 'preview' && !isLocalId(defect.id);
  const cannotDrawWhy = selected && selected.pending ? 'Not synced yet. Mark up once it has synced.' : null;

  const tile = {
    width: 72, height: 72, borderRadius: 6, flexShrink: 0,
    border: `1px solid ${theme.border}`, background: theme.bg,
  };
  const number = defect.defectNumber != null ? defect.defectNumber : displayId(defect.id);
  const notes = [
    preparing > 0 && { key: 'prep', colour: theme.textLight, text: `Preparing ${preparing} photo${preparing === 1 ? '' : 's'}…` },
    photoErr && { key: 'photo', colour: theme.danger, text: photoErr },
    saveErr && { key: 'save', colour: theme.danger, text: saveErr, attr: { 'data-sheet-save-error': '' } },
    loadErr && { key: 'load', colour: theme.danger, text: loadErr, attr: { 'data-sheet-load-error': '' } },
  ].filter(Boolean);

  return (
    // 2026-08-14 — a bottom sheet on a phone, a centred dialog on a desktop.
    // The alignment, corner radii and size all live in responsive.css
    // (.ss-sheet-backdrop / .ss-sheet / .ss-sheet--photos) because they change
    // at a breakpoint and an inline style cannot express one.
    <div
      onClick={() => { if (!editing) onClose(); }}
      onDragOver={swallowDrop}
      onDrop={swallowDrop}
      className="ss-sheet-backdrop"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', justifyContent: 'center',
        zIndex: 1500,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Defect #${number} photos`}
        onClick={e => e.stopPropagation()}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setPhotoDrop(!overChatBox(e)); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPhotoDrop(false); }}
        onDrop={onSheetDrop}
        className="ss-sheet ss-sheet--photos"
        style={{
          background: theme.surface, width: '100%',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header — the pin card's bar: the item, and a close. */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
          padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: theme.text }}>Defect #{number}</h2>
            {defect.description && (
              <div data-sheet-description style={{ fontSize: 13, color: theme.textLight, marginTop: 2, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                {defect.description}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 28, height: 28, borderRadius: 6, flexShrink: 0,
              border: 0, background: 'transparent',
              color: theme.textLight, cursor: 'pointer',
              fontSize: 18, lineHeight: 1, padding: 0,
            }}
          >×</button>
        </div>

        <PhotoMarkupStage
          key={selected ? selected.id : 'none'}
          photo={selected}
          index={selectedIdx}
          count={count}
          onPrev={prev}
          onNext={next}
          canDraw={canDraw}
          cannotDrawWhy={cannotDrawWhy}
          busy={busy}
          onSave={onSave}
          onRestore={onRestore}
          onEditingChange={setEditing}
        />

        {notes.length > 0 && (
          <div style={{ padding: '6px 16px 0', flexShrink: 0 }}>
            {notes.map(n => (
              <div key={n.key} {...(n.attr || {})} style={{ fontSize: 12, color: n.colour, lineHeight: 1.35 }}>{n.text}</div>
            ))}
          </div>
        )}

        {/* The filmstrip, which is also the drop box. */}
        <input ref={libraryInputRef} type="file" accept="image/*" multiple onChange={onPhotoFiles} style={{ display: 'none' }} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={onPhotoFiles} style={{ display: 'none' }} />
        <div
          ref={stripRef}
          data-photo-drop={photoDrop ? 'over' : 'idle'}
          title="Drag images here from your computer, or tap Choose photos"
          style={{
            display: 'flex', gap: 6, padding: 8, margin: '8px 16px 12px', flexShrink: 0,
            overflowX: 'auto', borderRadius: 8,
            border: '2px dashed ' + (photoDrop ? theme.brand : theme.border),
            background: photoDrop ? theme.bg : 'transparent',
            transition: 'border-color 0.12s, background 0.12s',
          }}
        >
          {sheetPhotos.map((p, i) => (
            <button
              type="button"
              key={p.id ?? i}
              onClick={() => { if (!editing) setSelectedIdx(i); }}
              disabled={editing && i !== selectedIdx}
              aria-label={`Photo ${i + 1} of ${count}`}
              aria-current={i === selectedIdx ? 'true' : undefined}
              data-strip-photo={i}
              data-strip-selected={i === selectedIdx ? '' : undefined}
              data-photo-pending={p.pending ? '1' : undefined}
              style={{
                ...tile, position: 'relative', overflow: 'hidden', padding: 0, cursor: 'pointer',
                outline: i === selectedIdx ? `2px solid ${theme.brand}` : 'none', outlineOffset: 1,
                opacity: editing && i !== selectedIdx ? 0.45 : 1,
              }}
            >
              <img
                loading="lazy"
                src={p.thumbnailUrl || p.url}
                alt={p.caption || `photo ${i + 1}`}
                draggable={false}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {/* v0.261.0 — on this device until the queue sends it (the pin card's band). */}
              {p.pending && (
                <span
                  title="Saved on this device — not synced yet"
                  style={{
                    position: 'absolute', left: 0, right: 0, bottom: 0,
                    fontSize: 8, fontWeight: 700, letterSpacing: 0.3, textAlign: 'center',
                    padding: '1px 0', background: theme.warn, color: 'white',
                    textTransform: 'uppercase',
                  }}
                >not synced</span>
              )}
            </button>
          ))}
          {[
            { key: 'camera', onClick: () => cameraInputRef.current?.click(), title: 'Take a photo with the camera', label: 'Take photo', Icon: Camera },
            { key: 'library', onClick: () => libraryInputRef.current?.click(), title: 'Choose photos from the library', label: 'Choose photos', Icon: Images },
          ].map(({ key, onClick, title, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              title={title}
              disabled={editing}
              style={{
                ...tile,
                border: `1px dashed ${theme.border}`,
                color: theme.textLight, cursor: 'pointer',
                fontSize: 10, fontWeight: 600, lineHeight: 1.15,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: 3, padding: 2, textAlign: 'center',
                fontFamily: 'inherit',
              }}
            >
              <Icon size={20} strokeWidth={2} aria-hidden />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* v0.127.0 — a client question waiting on this item is answered
            here (the Question chip opens this sheet); it scrolls itself
            into view. No question, no conversation: this is the photo sheet. */}
        {defect.lastCommentRole === 'recipient' && (
          <div style={{ padding: '0 16px 14px', overflowY: 'auto', maxHeight: '35vh', flexShrink: 0 }}>
            <DefectConversation defectId={defect.id} scrollIntoViewOnLoad />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter chip group — replaces InSpec's <select> elements with click-to-toggle
// pills, because chips read at a glance ("Open" stays visible) where a
// dropdown collapses the chosen value into a single line of small text.
function ChipGroup({ label, value, onChange, options }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <FilterLabel>{label}</FilterLabel>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map(opt => {
          const active = value === opt.value;
          const borderColor = opt.color || theme.border;
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              style={{
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 999,
                border: `1px solid ${active ? borderColor : theme.border}`,
                background: active ? (opt.color ? hexAlpha(opt.color, 0.15) : theme.bg) : theme.surface,
                color: active && opt.color ? opt.color : theme.text,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// v0.298.0 — the project team, on the register's header beside Status; a
// press on anyone opens the Team box, where people are added and removed. The
// three states read differently: reading, could not be read (Try again —
// never "no one"), and genuinely nobody yet (Add).
//
// v0.302.0 — Brett: "split the group visually on droppin so we can be
// reassured they are two seperate groups. We dont need to display the full
// name, just display that on hover." The team is drawn by the chat each person
// is in — Client (clients, consultants) and Subcontractors (subcontractors,
// suppliers) — each its own boxed group, both always shown, so the two never
// read as one. Staff (both chats) and people with no contact type (the Share
// box asks) get a box of their own only when someone is in it. Each person is
// a round badge of initials; name, company and email on hover. Past
// TEAM_SHOWN in a group, "+N".
const TEAM_SHOWN = 6;
// v0.307.0 — Scan Floor / Scan Videos hidden from the register's buttons.
const SHOW_SCAN_BUTTONS = false;
const TEAM_GROUPS = [
  { chat: 'client', label: 'Client',         hint: 'In the Client chat',        add: 'Add someone to the Client chat on this report' },
  { chat: 'trade',  label: 'Subcontractors', hint: 'In the Subcontractor chat', add: 'Add someone to the Subcontractor chat on this report' },
  { chat: 'all',    label: 'Staff',          hint: 'Staff: in both chats' },
  { chat: null,     label: 'No type',        hint: 'No contact type: the Share box asks which chat' },
];
// v0.314.0 — grouped by teamChat: a member set to the other chat on this
// project (the Team box's switch) sits in that chat's group here.
//
// v0.316.0 — Brett: "can i click here and add a subcontractor or add client
// directly". The Client and Subcontractors labels are buttons with a "+": a
// press opens the Team box with the contacts list up, and whoever is picked
// joins that chat on this project (onAdd(chat)).
//
// v0.300.0 — after the groups, "N with a link, not on the team" (warning
// colour) when anyone holds a live link without being on the team; a press
// opens the box, where they are listed and their links turned off. Links that
// could not be read say so with Try again. Nothing while they are being read
// or when everyone with a link is on the team.
function TeamStrip({ state, members, onOpen, onAdd, onRetry, linksState, stray, onRetryLinks }) {
  const list = Array.isArray(members) ? members : [];
  const pill = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '2px 10px', fontSize: 12, fontWeight: 600,
    borderRadius: 999, border: `1px solid ${theme.border}`,
    background: theme.surface, color: theme.text,
    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  };
  const badge = {
    border: 0, background: 'transparent', padding: 0, margin: 0,
    borderRadius: '50%', cursor: 'pointer', display: 'inline-flex',
  };
  const quiet = { fontSize: 12, color: theme.textLight };
  const link = {
    border: 0, background: 'transparent', padding: 0, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, color: theme.brand, fontFamily: 'inherit',
  };
  let body;
  if (state === 'failed' && !list.length) {
    body = (
      <span style={quiet}>
        The team could not be read.{' '}
        <button type="button" onClick={onRetry} style={link}>Try again</button>
      </span>
    );
  } else if (state === 'loading' && !list.length) {
    body = <span style={quiet}>Reading the team…</span>;
  } else if (!list.length) {
    body = (
      <span style={quiet}>
        No one on this report's team yet.{' '}
        <button type="button" onClick={onOpen} style={link}>Add team member</button>
      </span>
    );
  } else {
    const groups = TEAM_GROUPS
      .map(g => ({ ...g, people: list.filter(m => teamChat(m) === g.chat) }))
      .filter(g => g.people.length || g.chat === 'client' || g.chat === 'trade');
    body = (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {groups.map(g => {
          const shown = g.people.slice(0, TEAM_SHOWN);
          const more = g.people.length - shown.length;
          return (
            <div
              key={g.label}
              data-part="team-strip-group"
              data-group={g.chat || 'none'}
              title={g.hint}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                padding: '3px 8px 3px 10px', borderRadius: 8,
                border: `1px solid ${theme.border}`, background: theme.bg,
                minWidth: 0,
              }}
            >
              {g.add && onAdd ? (
                <button
                  type="button"
                  data-part="team-strip-add"
                  onClick={() => onAdd(g.chat)}
                  title={g.add}
                  aria-label={g.add}
                  style={{
                    border: 0, background: 'transparent', padding: 0, margin: 0, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit',
                    fontSize: 11, fontWeight: 700, color: theme.textLight, whiteSpace: 'nowrap',
                  }}
                >
                  {g.label} · {g.people.length}
                  <span aria-hidden="true" style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 16, height: 16, borderRadius: '50%', boxSizing: 'border-box',
                    border: `1px solid ${theme.brand}`, color: theme.brand,
                    fontSize: 13, fontWeight: 700, lineHeight: 1,
                  }}>+</span>
                </button>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, whiteSpace: 'nowrap' }}>
                  {g.label} · {g.people.length}
                </span>
              )}
              {g.people.length === 0 && <span style={{ ...quiet, fontSize: 11 }}>No one yet</span>}
              {shown.map(m => {
                const who = [m.name, m.company, m.email].filter(Boolean).join(' · ') || 'Unnamed';
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-part="team-strip-member"
                    onClick={onOpen}
                    title={who}
                    aria-label={who}
                    style={badge}
                  >
                    <ChatAvatar name={m.name || m.email} size={24} title="" />
                  </button>
                );
              })}
              {more > 0 && (
                <button type="button" onClick={onOpen} style={{ ...pill, padding: '1px 8px', fontSize: 11 }}
                  title={g.people.slice(TEAM_SHOWN).map(m => m.name || m.email).join(', ')}
                >+{more}</button>
              )}
            </div>
          );
        })}
        {state === 'failed' && (
          <span style={{ ...quiet, alignSelf: 'center' }}>
            Could not refresh.{' '}
            <button type="button" onClick={onRetry} style={link}>Try again</button>
          </span>
        )}
      </div>
    );
  }
  const strayCount = Array.isArray(stray) ? stray.length : 0;
  let linksNote = null;
  if (linksState === 'failed') {
    linksNote = (
      <span data-part="team-strip-links" data-state="failed" style={quiet}>
        Who has a link could not be read.{' '}
        <button type="button" onClick={onRetryLinks} style={link}>Try again</button>
      </span>
    );
  } else if (strayCount) {
    linksNote = (
      <button
        type="button"
        data-part="team-strip-links"
        data-state="stray"
        onClick={onOpen}
        title={'Has a link, not on the team: ' + stray.map(g => g.email || 'anyone with the link').join(', ')}
        style={{ ...pill, borderColor: theme.warn, color: theme.warn, whiteSpace: 'normal', textAlign: 'left' }}
      >{strayCount} with a link, not on the team</button>
    );
  }
  return (
    <div data-part="team-strip" data-state={state} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, maxWidth: '100%', marginLeft: 'auto' }}>
      <FilterLabel>Team{list.length ? ` · ${list.length}` : ''}</FilterLabel>
      {body}
      {linksNote && <div>{linksNote}</div>}
    </div>
  );
}

function FilterLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      textTransform: 'uppercase', color: theme.textLight,
    }}>{children}</div>
  );
}

// ---------------------------------------------------------------------------
// Status / priority pill. Same dot-on-tint visual as PlanScreen's defect
// markers so a row reads as "the same thing" as the canvas pin.
function Pill({ color, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 11, fontWeight: 700,
      padding: '2px 9px', borderRadius: 999,
      color: color,
      background: hexAlpha(color, 0.14),
      whiteSpace: 'nowrap',
      textTransform: 'capitalize',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block' }} />
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Empty state - v0.30.0 — Brett: "Make the panel look like a table that
// is awaiting data. Not just a big white box." Now renders the actual
// table header row with faded ghost cells beneath so the operator
// recognises the structure they'll fill, and the "no defects" / "filtered
// out" message is centered over the ghost rows as a soft overlay. No
// duplicate Open Plan button — the three motto buttons in the header
// already cover the "next action".
function EmptyState({ hasAny, onClearFilters }) {
  const message = hasAny
    ? { title: 'No defects match your filters', body: 'Try clearing them, or widen the search.', cta: 'Clear filters', icon: <Search size={32} color={theme.textLight} strokeWidth={1.5} /> }
    : { title: 'No defects yet', body: 'Drop a pin on the plan to raise your first defect — it\'ll show up here.', cta: null, icon: <MapPin size={32} color={theme.brand} strokeWidth={1.5} /> };
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden', position: 'relative' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
              <th style={{ width: 40, padding: '10px 8px' }} />
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  style={{
                    width: col.width || undefined,
                    minWidth: col.minWidth || undefined,
                    padding: '10px 10px',
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: theme.textLight,
                    whiteSpace: 'nowrap',
                  }}
                >{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4].map(i => (
              <tr key={i} style={{ borderBottom: `1px solid ${theme.border}`, height: 38 }}>
                <td />
                {COLUMNS.map(col => (
                  <td key={col.key} style={{ padding: '10px 10px' }}>
                    <div style={{
                      height: 10, borderRadius: 4,
                      background: theme.bg,
                      width: col.key === 'description' ? '70%'
                          : col.key === 'preview' ? 28
                          : '60%',
                      opacity: 0.7,
                    }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Overlay message centered over the ghost rows. */}
      <div style={{
        position: 'absolute', inset: 0, top: 44 /* clear the header */,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <div style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 10,
          padding: '18px 22px',
          maxWidth: 420,
          textAlign: 'center',
          boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
          pointerEvents: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>{message.icon}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>{message.title}</div>
          <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4, lineHeight: 1.45 }}>{message.body}</div>
          {message.cta && (
            <button onClick={onClearFilters} style={{ ...btnPrimary, marginTop: 10, fontSize: 12, padding: '6px 12px' }}>{message.cta}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers

// CSV field escape: wrap in quotes if it contains comma / quote / newline,
// double up embedded quotes per RFC 4180.
function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// Approximate a colour-at-alpha. We only feed hex from theme.js so this is
// the simple #rrggbb path - no rgba()/named-colour handling. Good enough for
// the pill backgrounds; if we ever pass a non-hex we silently get the
// rgba(0,0,0,a) fallback which is still readable.
function hexAlpha(hex, alpha) {
  if (typeof hex !== 'string' || !hex.startsWith('#') || hex.length !== 7) {
    return `rgba(0,0,0,${alpha})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
